/**
 * AtlasVehicleModule — the vehicle wave, client side.
 *
 * · HOVERCRAFT: ride any crafted hover_* item you own (X in the open, or the
 *   inventory Ride button). The module owns the rig while riding — raycast-
 *   style hover over the rendered terrain (and the water: a skimmer skims),
 *   catalog speed scaled by the machine's crafted quality, positions streamed
 *   through vehicleMove (the walking validator would reject skimmer speed).
 * · SHIP FLIGHT: board your berthed ship (X beside the hull; from a city core
 *   the hangar lends it) and fly the Commons sky with the SECTOR'S OWN
 *   stick — held W/S throttle, mouse steers the nose, A/D roll, R levels,
 *   SHIFT boost, overcruise on a steady full throttle, SPACE/click fires
 *   the capacitor-fed cannons (server-validated strikes on wilds
 *   creatures), all on the ship's REAL stats (chassis × components). The
 *   ground and the settlement skylines are SOLID: hard impacts damage the
 *   shield then the hull, and a breached hull crashes (crashShip — the
 *   wreck settles on lawful ground, an escape-pod-style fee applies).
 *   X lands anywhere the landing law allows; Shift+X recalls; climb past
 *   the ascent band and the sky hands you to the sector.
 * · REMOTE VEHICLES: other pilots' animState ('ride:<key>' / 'fly:<chassis>')
 *   dresses their avatar with the matching hull, and every parked berth in
 *   the world renders its ship.
 *
 * The avatar KCC/reconcile/move-sends stand down behind atlas.hoverRide /
 * atlas.skyFlight (the spaceFlight pattern); on dismount the reconcile
 * hard-snap re-seats the physics capsule at the vehicle's last position.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { HOVERCRAFT, hovercraftSpeed, hovercraftBoostSpeed, type HovercraftSpec } from '../lib/hovercraft_catalog.js';
import { CHASSIS, DEFAULT_CHASSIS, shipStats, type ShipStats } from '../lib/fleet.js';
import { cockpitEye, CockpitOverlay } from '../lib/cockpit.js';
import { zoomCamera, zoomStep, chaseFor, ZOOM_CHASE, ZOOM_MIN } from '../lib/cockpit_zoom.js';
import { hoverZoomStep } from '../lib/foot_camera.js';
import { structureHitAt, structurePushOut, structureSolidCount, addStructureSolid, clearStructureSolids } from '../lib/structure_solids.js';
import { hullBlockedAt } from '../lib/structure_masks.js';
import { airTargetAt, damageAirTarget } from '../lib/air_targets.js';
import { impactDecals, impactDecalsIfAny, structureFaceNormal, structureFacePoint, groundNormal, surfacePointOn } from '../lib/impact_decals.js';
import { reportDamageHit, reportGroundHit, renderToAbsolute, reattachDamageMover } from '../lib/damage_runtime.js'; // shader gaps R2: persistent material damage
import { activePreset } from '../lib/quality_tiers.js';
import { waterLiveAt, waterStillAt, waterSlopeAt, stampWater, splashWater, waterFx } from '../lib/water_fx.js';
import { hullTiltOnSurface, hullWindAccel, wakeStampPair, splashFor, HULL_ROCK_RATE } from '../lib/water_physics.js';
import {
  firstWaterCrossing, waterShotFor, downwashFor, newDownwash, sprayThisTick, breachSplash, hoverSprayMix, skimImpactMps, RateGate,
  SHIP_SKIM_M, SKIM_DRAG_PER_S, DOWNWASH_RING_EVERY_S, HOVER_ON_WATER_M, WATER_SHOT_REF_M, WATER_SHOT_MAX_M,
} from '../lib/water_interact.js';
import { reportCraft } from '../lib/audio/craft_feed.js';
import type { WeatherModule } from './WeatherModule.js';
import { createHeatHazeMaterial } from 'enginex/vfx/HeatHazeMaterial.js';
import { hazeSceneColor } from '../lib/vfx/fire_volume.js'; // surfaces (2026-09-27): THE HAZE COPY
import { createShieldMaterial } from 'enginex/vfx/ShieldMaterial.js';
import { hazeStrength, shieldIntensity, stage3Stats } from '../lib/stage3_fx.js';
import { type HoverReadout, stepHover, mouseSteerStep, type HoverState, type HoverInput, VEHICLE_SPEED_SCALE } from '../lib/hover_physics.js';
import { padRide, padRideFromSnapshot, type PadRide } from '../lib/gamepad_ride.js';
import { comfort } from '../lib/xr_comfort.js';
import { padSnap, padCached, padHeld, padLook, padRumble, padXrActive } from '../lib/gamepad.js';
import { keyHeldIn } from '../lib/keybinds.js';
import { HOVER_MAX_VY } from '../lib/hover_physics.js';
import { CombatVfx, explosionSoundDelayS } from './living-events/CombatVfx.js';
import { groundYIn } from '../lib/grounding.js';
import { prefabAssetId } from '../lib/player_models.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { settlementSites } from '../../../shared/settlements.js';
import { markShared, deferDisposeTree } from '../lib/defer_dispose.js';
import { VehicleFx } from '../lib/vehicle_fx.js';
import { hoverSeat, seatHullOffset, seatHullShift, RIDER_SEAT_M } from '../lib/hovercraft_visuals.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { cloudTunnel } from '../lib/cloud_tunnel.js';
import { sfx, sfxAt, sfxLoop, sfxLater, inverseDistanceGain } from '../lib/sfx.js';
import { wallImpactSound, wallImpactFeel, impactGateOpen, newImpactGate, newScrape, scrapeStep, hitJitter, HOVER_MASS_T, WALL_MASS_REF_T, SHIP_MASS_T } from '../lib/wall_contact.js';
import { cameraImpulse, addTrauma, stepFeel } from '../lib/combat_feel.js';
import { cameraMotionScale, scaleOffsets } from '../lib/camera_comfort.js';
import { newChase, resetChase, stepChase, orbitChase } from '../lib/chase_camera.js';
import type { AssetModule } from './AssetModule.js';

const FIXED_DT = 1 / 60;
const SEND_EVERY_TICKS = 12;      // 5 Hz position stream (the spaceMove cadence)
const ASCEND_Y = 4800;            // MIRROR: server ASCEND_MIN_Y_M — the handoff altitude (x4, 2026-08-23)
const ASCEND_WARN_Y = 3800;       // "the sky thins" HUD band
// The cloud deck: white-out ramps through this band so the world swap at
// ASCEND_Y happens fully inside cloud (the transition IS the loading screen).
const CLOUD_BASE_Y = 3400;
const CLOUD_FULL_Y = 4400;
const LAND_MAX_AGL = 30;          // X lands only this close to the ground
const BOARD_RANGE_M = 14;         // MIRROR: server BOARD_RANGE_M
const HANGAR_RANGE_M = 260;       // MIRROR: server HANGAR_RANGE_M
// ── Space-parity flight (the SpaceModule JTL model, in atmosphere) ─────────
const THROTTLE_RATE = 0.6;        // throttle change /s while W or S is held
const ROLL_RATE = 1.9;            // rad/s on A/D (Q/E too)
const LEVEL_RATE = 3.0;           // /s wing-leveling slerp while R is held
const ACCEL_K = 2.2;              // /s exponential chase toward the commanded velocity
const BOOST_MULT = 2.5;           // booster speed multiplier (burst)
const BOOSTER_MAX = 180;          // a longer reservoir → ~10 s of held boost (2026-08-27)
const BOOSTER_DRAIN = 18;         // /s while boosting (180/18 = 10 s of sustained boost)
const BOOSTER_REGEN = 12;         // /s while not (refills in ~15 s)
const CRUISE_MAX = 10;            // overcruise wind-up ceiling
const CRUISE_RAMP = 0.42;
const CRUISE_DECAY = 3.5;
const MOUSE_TURN = 0.0022;        // rad per px of pointer-locked mouse travel
const FIRE_COOLDOWN_S = 0.16;
const CAP_VOLLEY = 9;
const CAP_REGEN = 16;
const BOLT_SPEED = 420;
const BOLT_LIFE_S = 1.6;
const SHIP_CANNON_RANGE_M = 300;  // MIRROR: server SHIP_CANNON_RANGE_M
const FLY_MAX_MPS = 1400;         // total clamp, under the server's 1500 budget
const SHIP_HALF_W = 6;            // ship collision half-width (footprint margin)
/** The ship hull's structure probes along its heading: nose, centre, tail (2026-09-23: a single centre
 *  point let the nose bury itself in a tower before the centre noticed; the hovercraft already probes so). */
const SHIP_PROBES = [SHIP_HALF_W * 1.5, 0, -SHIP_HALF_W * 1.5] as const;
const HOVER_BLOCK_MARGIN = 1.0;  // per-PROBE half-width (2026-09-16: was 2.2 around a single centre point — the hull is probed at nose/centre/tail now, so the margin is a skin, not the whole craft)
/** Nose/tail probe offset along the heading — half the normalized hull length. */
const HOVER_HULL_HALF_L = 4.5;
/** The lever arms the hull is probed at, nose first (the deepest hit wins). */
const HULL_PROBES = [HOVER_HULL_HALF_L, 0, -HOVER_HULL_HALF_L] as const;
/** THE SEAT (2026-10-08): a craft whose rider sits off its hull's centre is DRAWN with the hull moved along the heading
 *  (hovercraft_visuals.HOVER_SEATS), so the three probes move with it — `shift` = how far the hull's centre lies ahead of
 *  the rider. 0 is the shared table above, bit for bit (every craft without a seat row). */
const SHIFTED_PROBES = new Map<number, readonly number[]>();
function hullProbesFor(shift: number): readonly number[] {
  if (shift === 0) return HULL_PROBES;
  let p = SHIFTED_PROBES.get(shift);
  if (!p) { p = [HOVER_HULL_HALF_L + shift, shift, -HOVER_HULL_HALF_L + shift]; SHIFTED_PROBES.set(shift, p); }
  return p;
}
/** A rider who sits HIGH (a deck-fit `hoverM`) must not lift the hull's wall test over a low wall, nor out of the
 *  ground-level wall mask's band: past this ride height — today's tallest founding craft — the resolver is asked at the
 *  height a craft of that ride height would be. Every founding craft is at or under it (the lift is exactly 0). */
const HOVER_SEAT_BAND_M = 1.3;

/**
 * The hovercraft ↔ structure resolver handed to `stepHover` (2026-09-16).
 *
 * The hull is probed at NOSE, CENTRE and TAIL: a 9 m craft whose centre point is
 * clear can still have its nose buried in a wall, and a single centre probe made
 * every contact a parallel slide — a corner clip could never turn the craft. The
 * deepest probe wins; its push-out delta moves the CENTRE, and its lever arm goes
 * back so the integrator can yaw the hull off the corner it caught.
 *
 * `agl` is the hull's height above the ground: below the walking band (2.6 m) the
 * probes ask the ground-level WALL MASK first (lib/structure_masks.ts — the walls
 * of the collider mesh, so a compound's internal streets the old base→roof box
 * sealed are open and its real walls still stop the craft), and fall back to the
 * band solids where no mask covers (2026-09-23).
 *
 * Module-level so the in-browser self-test drives the same code the ride does;
 * the body is the pure `hullBlockedAt` the Node harness pins.
 */
function hoverBlocked(x: number, z: number, y: number, yaw: number, agl: number, shift = 0): { x: number; z: number; lever?: number } | null {
  return hullBlockedAt(x, z, y, yaw, agl, hullProbesFor(shift), HOVER_BLOCK_MARGIN);
}
const IMPACT_SAFE_MPS = 16;       // touch the ground softer than this = no harm
const SHIP_MODEL_YAW = -Math.PI / 2; // GLB hulls are authored nose −X (SpaceModule law)
// Image-to-3D hover hulls came out of the three-quarter-view concepts with the
// nose left of travel — field-tuned 2026-08-20 in two steps to a full 90° right.
const HOVER_MODEL_YAW = -Math.PI / 2;
const HOVER_MODEL_LEN_M = 9.0; // normalized hull length (was 3.0 — read as a toy in the field)
// Third-person chase offsets (camera local to the rig; behind = +Z, body faces −Z).
const HOVER_CAM_OFFSET = { y: 2.6, z: 8.5 };
const FLY_CAM_OFFSET = { y: 3.6, z: 12 };

/** MIRROR: server LAND_EXCLUDE_M — settlement no-landing exclusion radii (m). */
const LAND_EXCLUDE_M: Record<string, number> = { city: 460, town: 210, hamlet: 110, capital: 3400, district: 0, circuit: 520 }; // capital wave: the whole enclosure (wall 3230 + margin); districts are covered by it; the stadium disc

// ── Shared cannon-bolt assets ────────────────────────────────────────────────
// ONE texture + ONE material for every bolt ever fired. The old fire() built
// a fresh mapless SpriteMaterial PER SHOT: no map = a plain white SQUARE on
// screen, and each new material = a new WebGPU pipeline compile — holding the
// trigger collapsed the frame rate to 3-5 fps (user report 2026-08-23).
let boltMat: THREE.SpriteMaterial | null = null;
function boltMaterial(): THREE.SpriteMaterial {
  if (boltMat) return boltMat;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 30);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.25, 'rgba(150,225,255,0.95)');
  grad.addColorStop(0.7, 'rgba(70,170,255,0.35)');
  grad.addColorStop(1, 'rgba(70,170,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  boltMat = markMrtOverlay(new THREE.SpriteMaterial({ map: tex, color: 0xbfeaff, transparent: true, opacity: 0.95, depthWrite: false, blending: THREE.AdditiveBlending }));
  return boltMat;
}

const TIER_COLOR: Record<number, number> = {
  1: 0x7a6a52, 2: 0x5a7a5f, 3: 0x4f6f9a, 4: 0x7a4f9a, 5: 0x9a4f5f,
};


/** True while the local player walks the boardable sky-ship's decks (the
 *  altitude-interior regime — `SkyShipModule` sets `skyship:<siteId>`). */
function aboardSkyShip(): boolean {
  return atlas.interiorId !== null && atlas.interiorId.startsWith('skyship:');
}

export class AtlasVehicleModule implements IModule {
  readonly name = 'AtlasVehicle';

  private app: App | null = null;
  private assets: AssetModule | null = null;

  // mode
  private mode: 'none' | 'hover' | 'fly' = 'none';
  private hoverItem: { id: bigint; key: string; quality: number } | null = null;
  private flyChassis = '';

  // integration state
  private velX = 0; private velY = 0; private velZ = 0;
  private sendAcc = 0;
  private bob = 0;
  private ascending = false;
  private resumeChecked = false;

  // space-parity flight state (the SpaceModule JTL model)
  private flyQuat = new THREE.Quaternion();
  private throttle = 0;
  private cruise = 1;
  private boosterE = BOOSTER_MAX;
  private stats: ShipStats = shipStats(DEFAULT_CHASSIS, []);
  private statsAcc = 0;
  private hull = 100;
  private shield = 0;
  private cap = 0;
  private lastHitT = 0;
  private fireCool = 0;
  private steerRate = 0;
  private wasBoosting = false;
  private crashing = false;
  /** Wall contact, heard (physics follow-up A): the impact cooldown on the FIXED clock, and the grind loop's state. */
  private readonly wallGate = newImpactGate();
  private wallScrape = newScrape();
  private wallSimMs = 0;
  private bolts: Array<{ sprite: THREE.Sprite; vel: THREE.Vector3; ttl: number }> = [];
  /** Retired bolt sprites, reused instead of disposed (shared material — never per-shot). */
  private boltPool: THREE.Sprite[] = [];
  /** Hit VFX (muzzle/bolt-impact explosions on terrain/structures) — lazily built. */
  private vfx: CombatVfx | null = null;
  private readonly _hit = new THREE.Vector3();
  /** collideStructure's heading scratch (never `_v1` — the flight loop holds `desired` in it across the call). */
  private readonly _hdg = new THREE.Vector3();
  private readonly _n = new THREE.Vector3();
  private readonly _n2 = new THREE.Vector3();
  private readonly _rayFrom = new THREE.Vector3();
  private fxHitCount = 0; // diagnostic: explosions spawned by bolt/collision hits
  // Hovercraft physics readout for the mesh (bank into turns, pitch on ramps/climb).
  private hoverBank = 0;
  private hoverPitch = 0;
  private hoverAirborne = false;
  /** Interactive water: the hull's rock on the surface slope (slewed) and whether it was over water last tick. */
  private waterBank = 0;
  private waterPitch = 0;
  private wasOnWater = false;
  private static readonly HULL_W = 2.2;
  private static readonly HULL_L = 4.2;
  /** W2 (2026-10-05): the ship over water — the spray carry, the ring gate, the breach latch, the diag. */
  private readonly washDebt = { debt: 0 };
  private readonly washOut = newDownwash();
  private readonly sprayMix = { gain: 0, rate: 1 };
  /** Report ids per remote (a template string per remote per frame was garbage on the hot path). */
  private readonly remoteCraftIds = new Map<string, string>();
  private readonly washGate = new RateGate();
  private washAngle = 0;
  private skimming = false;
  private breachArmed = true;
  private readonly washDiag = { over: false, heightM: -1, k: 0, gain: 0, skimming: false, hullM: 0 };

  /**
   * A cushion craft over open water (2026-09-11): it rocks on the live
   * surface slope, the wind pushes it downwind, its downwash presses a dish
   * the field trails into a wake (water raised ahead, lowered behind — the
   * Frostbite pair), and a landing on water splashes.
   */
  private rideOnWater(r: HoverReadout, fluid: boolean): void {
    if (!fluid || !r.onWater) {
      this.waterBank *= 0.9; this.waterPitch *= 0.9;
      this.hoverBank += this.waterBank; this.hoverPitch += this.waterPitch;
      this.wasOnWater = false;
      return;
    }
    const x = atlas.localX, z = atlas.localZ, yaw = atlas.localYaw;
    const sl = waterSlopeAt(x, z);
    const tilt = hullTiltOnSurface(sl.dx, sl.dz, yaw);
    const k = Math.min(1, HULL_ROCK_RATE * FIXED_DT);
    this.waterBank += (tilt.bank - this.waterBank) * k;
    this.waterPitch += (tilt.pitch - this.waterPitch) * k;
    this.hoverBank += this.waterBank; this.hoverPitch += this.waterPitch;
    const w = (this.weatherMod ??= this.app?.getModule?.('Weather') as WeatherModule | undefined)?.wind?.(); // cached: a linear module scan per fixed tick otherwise
    if (w) { const a = hullWindAccel(w.x, w.z, w.strength); this.velX += a.ax * FIXED_DT; this.velZ += a.az * FIXED_DT; }
    const pair = wakeStampPair(r.speed, AtlasVehicleModule.HULL_W, AtlasVehicleModule.HULL_L);
    const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
    stampWater(x + fx * pair.behind.along, z + fz * pair.behind.along, pair.behind.strength, pair.behind.radius);
    if (r.speed > 1.5) stampWater(x + fx * pair.ahead.along, z + fz * pair.ahead.along, pair.ahead.strength, pair.ahead.radius);
    if (!this.wasOnWater || r.landed > 18) {
      const live = waterLiveAt(x, z);
      if (live !== null) {
        const sp = splashFor(Math.max(r.landed > 18 ? 6 : 3, r.speed * 0.3), 2.5);
        splashWater(x, live, z, sp.count, sp.speed);
        stampWater(x, z, sp.stamp.strength, sp.stamp.radius);
      }
    }
    this.wasOnWater = true;
  }
  private hoverDrifting = false;      // rear loose this tick (handbrake at speed, or the grip let go)
  private hoverNatural = false;       // the drift is NATURAL (friction-circle saturation, no key)
  private hoverOnWater = false;       // riding a wake over open water (capped at HOVER_WATER_TOP)
  private hoverDriftCharge = 0;       // 0..1 mini-turbo charge (HUD)
  private hoverTurboPrev = 0;         // last tick's mini-turbo remaining (rising-edge SFX)
  private hoverMouseSteer = 0;        // persistent mouse steering axis (-1..1, self-centring) — the mouse steers the wheel
  private padLast: PadRide | null = null;   // last ride mapping result, for the diagnostic
  private readonly _v1 = new THREE.Vector3();
  private readonly _q1 = new THREE.Quaternion();
  private readonly _m1 = new THREE.Matrix4();

  /** Pointer-locked mouse steers the nose while flying (the space contract). */
  private onMouseMove = (e: MouseEvent): void => {
    if (this.mode !== 'fly' || document.pointerLockElement === null) return;
    this.flyQuat.multiply(this._q1.setFromAxisAngle(this._v1.set(0, 1, 0), -e.movementX * MOUSE_TURN));
    this.flyQuat.multiply(this._q1.setFromAxisAngle(this._v1.set(1, 0, 0), -e.movementY * MOUSE_TURN));
    this.steerRate = Math.min(1.6, Math.abs(e.movementX * 0.06) + Math.abs(e.movementY * 0.06));
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (this.mode === 'fly' && e.button === 0 && document.pointerLockElement !== null) this.fire();
  };

  /** Scroll wheel = continuous camera zoom while flying: in → first-person
   *  cockpit, out → a wide chase. (Hover uses a fixed chase.) */
  private onWheel = (e: WheelEvent): void => {
    if (this.mode === 'hover') {
      // 2026-09-09: the hovercraft chase zooms too ("see where you are going
      // with a bigger view") — the avatar module poses the camera from
      // atlas.hoverCamBack every frame (lib/foot_camera hoverCameraPose).
      e.preventDefault();
      const next = hoverZoomStep(atlas.hoverCamBack, e.deltaY);
      if (next !== atlas.hoverCamBack) { atlas.hoverCamBack = next; try { localStorage.setItem('atlas:hover-cam-back', String(next)); } catch { /* fine */ } }
      return;
    }
    if (this.mode !== 'fly') return;
    e.preventDefault();
    this.flyCamBack = zoomStep(this.flyCamBack, e.deltaY);
    this.applyFlyZoom();
  };

  // input (flight tracks its own keys — intent flattens pitch)
  private keys = new Set<string>();

  // meshes
  private rideMesh: THREE.Object3D | null = null;
  /** The ride's drawn body (a war supply run's crates ride on it, 2026-09-24), or null on foot. */
  rideObject(): THREE.Object3D | null { return this.rideMesh; }
  /** Stage 3 VFX on the local ride: a heat-haze quad behind a hovercraft and
   *  a shield shell that flashes on a hull hit (both children of the ride mesh). */
  private rideHaze: { mesh: THREE.Mesh; strength: { value: number } } | null = null;
  private rideShield: { mesh: THREE.Mesh; intensity: { value: number } } | null = null;
  private shieldHitAt = -1;
  /** Engine plumes + dust for the LOCAL ride (fitted to the hull on mount). */
  private rideFx: VehicleFx | null = null;
  /** Same, per remote rider (keyed like remoteRigs). */
  private remoteFx = new Map<string, { fx: VehicleFx; lastX: number; lastZ: number; lastT: number; speed: number }>();
  private berthMeshes = new Map<string, { obj: THREE.Object3D; chassis: string; fallback: boolean }>();
  private remoteRigs = new Map<string, { obj: THREE.Object3D; tag: string; fallback: boolean }>();
  private shipMasters = new Map<string, THREE.Object3D>();
  private shipLoading = new Set<string>();
  private berthAcc = 0;

  // HUD
  private hud: HTMLDivElement | null = null;
  private hint: HTMLDivElement | null = null;

  private onKeyDown = (e: KeyboardEvent): void => this.onKey(e, true);
  private onKeyUp = (e: KeyboardEvent): void => this.onKey(e, false);
  /** Focus left the page: a held throttle / boost / steer key never gets its key-up here and
   *  kept the craft driving (a hovercraft boosting into a wall) until pressed again. */
  private onBlur = (): void => { this.keys.clear(); };
  private onVisibility = (): void => { if (document.hidden) this.keys.clear(); };

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('VehicleFixed', Phase.FixedSim, 43, () => this.fixed()));
    app.world.addSystem(makeSystem('VehicleRender', Phase.Animation, 38, (dt) => this.frame(dt)));
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('wheel', this.onWheel, { passive: false });
    try { const v = Number(localStorage.getItem('atlas:hover-cam-back')); if (Number.isFinite(v) && v > 0) atlas.hoverCamBack = hoverZoomStep(v, 0); } catch { /* fine */ }
    bus.on('atlas:world-entered', () => { this.onWorldEntered(); });
    // War-air return fire: the sky battle shoots back only while we FLY.
    bus.on('atlas:ship-damage', ({ amount, why }) => { if (this.mode === 'fly') this.damage(amount, why); });
    // The ride handoff (sky-ship boarding): step off the skimmer synchronously
    // so the lift finds the avatar back on its own capsule. Flight is NOT
    // ended here — the ship module refuses to board a flying player instead.
    bus.on('atlas:vehicle-dismount', () => { if (this.mode === 'hover') this.dismount(); });
    this.buildHud();
    // Probe/dev hook — mirrors __talk's shape.
    (window as unknown as Record<string, unknown>).__vehicle = {
      mount: (id?: string) => this.mountBest(id ? BigInt(id) : undefined),
      dismount: () => this.dismount(),
      board: () => this.board(),
      land: () => this.land(),
      recall: () => this.recall(),
      state: () => ({ mode: this.mode, y: atlas.localY, chassis: this.flyChassis, item: this.hoverItem?.key ?? '', hoverCamBack: atlas.hoverCamBack, onWater: this.hoverOnWater, waterBank: this.waterBank, waterPitch: this.waterPitch, bank: this.hoverBank, pitch: this.hoverPitch, speed: Math.hypot(this.velX, this.velZ), wall: { ...this.wallStats, gate: { played: this.wallGate.played, suppressed: this.wallGate.suppressed }, grinds: this.wallScrape.grinds, grindGain: +this.wallScrape.gain.toFixed(3) }, chase: { ...this.chaseDiag }, wash: { ...this.washDiag, heightM: +this.washDiag.heightM.toFixed(2), k: +this.washDiag.k.toFixed(3), gain: +this.washDiag.gain.toFixed(3) }, remoteWater: this.remoteWaterCount }),
      shieldFlash: () => { if (!this.rideShield) return false; this.shieldHitAt = performance.now(); stage3Stats.shieldHits++; return true; }, // stage 3 probe: a hit's flash without damage
      cockpit: (on?: boolean) => { if (this.mode !== 'fly') return false; const w = on ?? !this.cockpitView; this.flyCamBack = w ? ZOOM_MIN + 0.5 : chaseFor(CHASSIS[this.flyChassis]); this.applyFlyZoom(); return this.cockpitView; },
      // The pad, as the ride sees it. `present` false with a pad plugged in is
      // normal until the player presses something while the page is focused —
      // the Gamepad API hides devices until then, and needs https or localhost.
      gamepad: () => ({ ...padSnap(), ride: this.padLast }),
    };
    // Ship-combat diagnostics + a headless self-test of the collision/VFX paths
    // (full sky-flight is infeasible to drive in a headless boot).
    (globalThis as Record<string, unknown>).__hullScorchTest = (n = 12) => this.hullScorchTest(Math.max(1, Math.min(60, Number(n) || 12)));
    (window as unknown as Record<string, unknown>).__shipCombat = () => {
      const seed = this.commonsSeed();
      let ns: { id: number; x: number; z: number; d: number } | null = null;
      if (seed) {
        let best = Infinity;
        for (const s of settlementSites(seed)) {
          const d = Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ);
          if (d < best) { best = d; ns = { id: s.id, x: s.centerX, z: s.centerZ, d: Math.round(d) }; }
        }
      }
      return {
        mode: this.mode, flying: this.mode === 'fly',
        hull: Math.round(this.hull), shield: Math.round(this.shield),
        solids: structureSolidCount(), bolts: this.bolts.length,
        fxHits: this.fxHitCount, vfxCount: this.vfx?.count ?? 0,
        nearestSite: ns,
      };
    };
    (window as unknown as Record<string, unknown>).__shipCombatTest = () => this.shipCombatSelfTest();
    // Hovercraft physics: live readout + an in-browser self-test of the arcade
    // integrator (proves the SAME pure module the Node test covers runs in the
    // client bundle: emergent top speed, boost, the ramp launch, grip, reverse).
    (window as unknown as Record<string, unknown>).__hoverPhysics = () => ({
      mode: this.mode, riding: this.mode === 'hover',
      speed: Math.round(Math.hypot(this.velX, this.velZ) * 10) / 10,
      vy: Math.round(this.velY * 10) / 10, airborne: this.hoverAirborne,
      boost: Math.round(this.boosterE), bank: Math.round(this.hoverBank * 100) / 100,
      pitch: Math.round(this.hoverPitch * 100) / 100,
    });
    (window as unknown as Record<string, unknown>).__hoverPhysicsTest = () => {
      const checks: string[] = [];
      let pass = 0, fail = 0;
      const ok = (c: boolean, m: string): void => { if (c) { pass++; checks.push('✓ ' + m); } else { fail++; checks.push('✗ ' + m); } };
      const tune = { top: 40, accel: 2.2, hoverM: 1.2 };
      const flat = (): number => 0;
      const dt = 1 / 60;
      // top speed
      let s: HoverState = { x: 0, z: 0, y: tune.hoverM, vx: 0, vy: 0, vz: 0, yaw: -Math.PI / 2 };
      let rr = { speed: 0, airborne: false } as ReturnType<typeof stepHover>;
      for (let i = 0; i < 60 * 12; i++) rr = stepHover(s, { throttle: 1, steer: 0, handbrake: false, boost: false }, flat, tune, dt);
      ok(rr.speed > tune.top * 0.95 && rr.speed <= tune.top + 1, `emergent top speed ≈ ${tune.top} (${rr.speed.toFixed(1)})`);
      // ramp launch
      const ramp = (x: number): number => x < 6 ? 0 : x < 18 ? (x - 6) * 0.6 : x < 20 ? 7.2 : 0;
      s = { x: 0, z: 0, y: tune.hoverM, vx: 0, vy: 0, vz: 0, yaw: -Math.PI / 2 };
      let launched = false, peakGap = 0, down = false, landed = false;
      for (let i = 0; i < 60 * 8; i++) {
        const r = stepHover(s, { throttle: 1, steer: 0, handbrake: false, boost: true }, (x) => ramp(x), tune, dt);
        if (r.airborne && r.vy > 1) launched = true;
        peakGap = Math.max(peakGap, r.gap);
        if (launched && r.vy < -1) down = true;
        if (down && !r.airborne && Math.abs(r.gap - tune.hoverM) < 1.5) landed = true;
      }
      ok(launched && peakGap > 4, `launches off a ramp with real air (${peakGap.toFixed(1)} m)`);
      ok(down && landed, 'gravity brings it back down and it lands');
      // STRUCTURE collision via the REAL registry + blocked path (not a stub).
      const TID = -998, cx = 2e6, cz = 2e6;
      clearStructureSolids(TID);
      addStructureSolid(TID, { x: cx + 30, z: cz, hx: 20, hz: 20, yaw: 0, base: 0, top: 40 }); // a tower 30 m ahead (+x)
      // the SHIPPED multi-probe resolver, not a stub; the flat ground puts the hull at agl = y (< 2.6 m: the
      // mask is asked first, covers nothing out here, and the band-solid fallback must still stop the craft)
      const blk = (x: number, z: number, y: number, yaw: number): ReturnType<typeof hoverBlocked> => hoverBlocked(x, z, y, yaw, y - flat());
      s = { x: cx, z: cz, y: tune.hoverM, vx: 0, vy: 0, vz: 0, yaw: -Math.PI / 2 }; // forward = +x, toward the tower
      let maxX = cx;
      for (let i = 0; i < 60 * 6; i++) { stepHover(s, { throttle: 1, steer: 0, handbrake: false, boost: true }, flat, tune, dt, blk); maxX = Math.max(maxX, s.x); }
      clearStructureSolids(TID);
      // The NOSE stops at the tower's face (+10), so the CENTRE halts a hull
      // half-length short of it — the multi-probe hull, on the real registry.
      ok(maxX < cx + 10 - HOVER_HULL_HALF_L + 0.2 && maxX > cx + 10 - HOVER_HULL_HALF_L - 1.5,
        `the hovercraft is BLOCKED by a real registered structure, NOSE first (centre reached +${(maxX - cx).toFixed(1)} m; tower face +10, hull half-length ${HOVER_HULL_HALF_L})`);
      return { pass, fail, checks };
    };
    // TEST-ONLY: force-ride a hovercraft without server ownership so a headless
    // harness can prove the live mount→hoverFixed→physics path (the vehicleMove
    // stream is rejected for the fake item id and harmlessly caught).
    // Effects diagnostic: nozzle count + live dust motes + plume length of the local ride.
    (window as unknown as Record<string, unknown>).__vehicleFx = () => this.rideFx
      ? { nozzles: this.rideFx.nozzles.length, motes: this.rideFx.particleCount(), plume: Math.round(this.rideFx.currentPlumeLength() * 100) / 100, model: this.rideTag }
      : null;
    (window as unknown as Record<string, unknown>).__hoverForceRide = (key = 'hover_skiff') => {
      if (this.mode !== 'none' || !HOVERCRAFT[key as string] || aboardSkyShip()) return false; // mirrors mountBest's ship guard
      this.hoverItem = { id: 0n, key: key as string, quality: 400 };
      this.mode = 'hover';
      atlas.hoverRide = true;
      this.velX = this.velY = this.velZ = 0;
      this.hoverBank = this.hoverPitch = 0; this.hoverAirborne = false;
      this.sendAcc = 0;
      this.seatHover(HOVERCRAFT[key as string].hoverM);
      this.ensureRideMesh(`hover:${key}`);
      return true;
    };
    // TEST-ONLY (W2, 2026-10-05): fly the ship without the server's boarding so a headless harness can hold it still
    // `aboveM` over the live water (or the ground) and read the downwash; throttle 0 holds it where it is (this flight
    // model has no gravity). The flyMove stream is refused for a non-fly row and harmlessly caught.
    (window as unknown as Record<string, unknown>).__shipForceFly = (aboveM = 8) => {
      if (this.mode !== 'none' || atlas.spaceFlight || !this.onGroundWorld() || aboardSkyShip()) return false;
      this.enterFlight();
      this.throttle = 0;
      this.velX = this.velY = this.velZ = 0;
      const conn = atlas.conn;
      const g = conn ? groundYIn(conn, atlas.currentWorldId, atlas.localX, atlas.localZ) : atlas.localY;
      const w = waterLiveAt(atlas.localX, atlas.localZ) ?? waterStillAt(atlas.localX, atlas.localZ);
      atlas.localY = Math.max(g + 2.5, (w ?? -1e9) + SHIP_SKIM_M) + Math.max(0, Number(aboveM) || 0);
      return true;
    };
    (window as unknown as Record<string, unknown>).__shipForceLand = () => { if (this.mode !== 'fly') return false; this.exitFlight(); return true; };
    (window as unknown as Record<string, unknown>).__shipHold = (aboveM: number) => {
      // hold the ship `aboveM` over the water under it (probe lever; the flight integrator keeps it there at throttle 0)
      if (this.mode !== 'fly') return false;
      const w = waterLiveAt(atlas.localX, atlas.localZ) ?? waterStillAt(atlas.localX, atlas.localZ);
      if (w === null) return false;
      atlas.localY = w + SHIP_SKIM_M + Math.max(0, Number(aboveM) || 0); this.velY = 0; this.throttle = 0;
      return true;
    };
  }

  /**
   * Headless self-test: exercises the REAL collision + bolt-hit code paths
   * against a synthetic tower placed far from any settlement, asserting the
   * geometry, the collision damage + explosion, and the cannon-bolt explosion.
   */
  private shipCombatSelfTest(): { pass: number; fail: number; checks: string[] } {
    const checks: string[] = [];
    let pass = 0, fail = 0;
    const ok = (c: boolean, m: string): void => { if (c) { pass++; checks.push('✓ ' + m); } else { fail++; checks.push('✗ ' + m); } };
    const TID = -999, cx = 1e6, cz = 1e6, base = 100, top = 400;
    const solid = { x: cx, z: cz, hx: 20, hz: 20, yaw: 0, base, top };
    clearStructureSolids(TID);
    addStructureSolid(TID, { ...solid });
    // 1) geometry
    ok(structureHitAt(cx, cz, 250) !== null, 'a point inside the tower volume is a hit');
    ok(structureHitAt(cx, cz, top + 50) === null, 'a point above the roof is NOT a hit (fly over)');
    ok(structureHitAt(cx + 100, cz, 250) === null, 'a point outside the footprint is NOT a hit');
    const pushed = structurePushOut(solid, cx + 5, cz, 6);
    ok(Math.abs(pushed.x - cx) >= 20, 'push-out ejects the point clear of the footprint');
    // 2) collision → damage + explosion (real module path)
    const hull0 = this.hull; this.crashing = false; this.hull = this.stats.hp; this.shield = 0;
    const fx0 = this.fxHitCount;
    const res = this.collideStructure(cx + 5, cz, 250, 120);
    ok(res !== null, 'flying into the tower resolves a collision (ejected)');
    ok(this.hull < this.stats.hp, `the collision damaged the hull (${Math.round(this.hull)}/${this.stats.hp})`);
    ok(this.fxHitCount > fx0, 'the collision spawned a hit explosion');
    this.hull = hull0;
    // 3) cannon bolt → structure hit → explosion (real stepBolts path)
    const fx1 = this.fxHitCount;
    const sprite = this.boltPool.pop() ?? new THREE.Sprite(boltMaterial());
    sprite.visible = true; sprite.position.set(cx, 250, cz);
    this.app?.world.scene.add(sprite);
    this.bolts.push({ sprite, vel: new THREE.Vector3(0, 0, 0), ttl: 1 });
    const before = this.bolts.length;
    this.stepBolts(1 / 60);
    ok(this.fxHitCount > fx1, 'a cannon bolt striking the tower spawns an explosion');
    ok(this.bolts.length < before, 'the struck bolt retired');
    ok((this.vfx?.count ?? 0) > 0, 'the hit VFX has live sprites in the scene');
    clearStructureSolids(TID);
    return { pass, fail, checks };
  }

  constructor(assets: AssetModule | null = null) {
    this.assets = assets;
  }

  // ─── input ────────────────────────────────────────────────────────────────

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (down) this.keys.add(e.code); else this.keys.delete(e.code);
    if (!down || e.repeat) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    if (atlas.flyMode || atlas.spaceFlight || atlas.editorCursorMode || atlas.loadingGate) return;
    if (e.code === 'KeyY' && this.mode === 'fly' && !e.ctrlKey && !e.metaKey && !e.altKey) { this.toggleCockpit(); return; }
    if (e.code !== 'KeyX' || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.shiftKey) {
      // Shift+X: recall the parked ship (Commons berth network), or — on a
      // personal / spawned world where no berth exists — lift the ship off in
      // place so you can fly its atmosphere.
      if (this.inCommons()) { void this.recall(); }
      else if (this.mode === 'none' && this.onGroundWorld() && hasActiveShip()) { void this.board(); }
      return;
    }
    // Contextual X: land > dismount > board > mount.
    if (this.mode === 'fly') { void this.land(); return; }
    if (this.mode === 'hover') { this.dismount(); return; }
    if (this.nearOwnBerth() || this.nearCity()) { void this.board(); return; }
    void this.mountBest();
  }

  // ─── shared helpers ───────────────────────────────────────────────────────

  private inCommons(): boolean {
    return atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
  }

  /** ANY ground world the avatar can ride/fly on — the Commons, a personal
   *  world, or a newly-spawned one (never space, never the admin fly cam). */
  private onGroundWorld(): boolean {
    return atlas.currentWorldId !== null && !atlas.spaceFlight && !atlas.flyMode;
  }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn || atlas.commonsWorldId === null) return null;
    const w = conn.db.world.worldId.find(atlas.commonsWorldId);
    return w ? w.terrainSeed : null;
  }

  /** Terrain seed of the world the avatar is CURRENTLY in (any world). */
  private worldSeed(): string | null {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return null;
    const w = conn.db.world.worldId.find(atlas.currentWorldId);
    return w ? w.terrainSeed : null;
  }

  /** The floor to clamp the hover/ground sampler against: the Commons ocean on
   *  the Commons, none elsewhere (personal / spawned worlds render no sea). */
  private weatherMod: WeatherModule | undefined;

  private seaFloor(): number {
    if (!this.inCommons()) return -1e9;
    const seed = this.commonsSeed();
    return seed !== null ? commonsSeaLevel(seed) : -1e9;
  }

  private camPitch(): number {
    const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
    return cam ? cam.rotation.x : 0;
  }

  private status(text: string): void { bus.emit('atlas:status', { text }); }

  /** MIRROR of the server landing law: the blocking settlement tier, 'water', or null (clear).
   *  Only the Commons carries settlements + an ocean; personal / spawned worlds
   *  are always clear to set down on (the server agrees — landShip skips the law off-Commons). */
  private landBlocker(x: number, z: number): string | null {
    if (!this.inCommons()) return null;
    const seed = this.commonsSeed();
    if (!seed) return null;
    for (const s of settlementSites(seed)) {
      const r = LAND_EXCLUDE_M[s.tier] ?? 110;
      if (Math.hypot(s.centerX - x, s.centerZ - z) < r) return s.tier;
    }
    const conn = atlas.conn;
    const ground = conn ? groundYIn(conn, atlas.commonsWorldId, x, z) : 0;
    if (ground <= commonsSeaLevel(seed) + 0.5) return 'water';
    return null;
  }

  private nearOwnBerth(): boolean {
    const conn = atlas.conn;
    if (!conn?.identity || !this.inCommons()) return false;
    const b = conn.db.shipBerth.identity.find(conn.identity);
    return !!b && Math.hypot(b.posX - atlas.localX, b.posZ - atlas.localZ) <= BOARD_RANGE_M;
  }

  private hasBerth(): boolean {
    const conn = atlas.conn;
    return !!(conn?.identity && conn.db.shipBerth.identity.find(conn.identity));
  }

  /** nearCity memo: the 312-site sweep answers only for the position it was taken at. */
  private nearCityAt = { x: NaN, z: NaN, seed: '', near: false };
  private nearCity(): boolean {
    const seed = this.commonsSeed();
    if (!seed || !this.inCommons() || this.hasBerth()) return false;
    // Every Animation frame (the on-foot HUD hint) walked all 312 sites; the
    // answer only changes when the player MOVES — reuse it while they haven't
    // (0.25 m is far inside the 1-frame walk step at the hangar radius).
    const m = this.nearCityAt;
    if (m.seed === seed && Math.abs(m.x - atlas.localX) < 0.25 && Math.abs(m.z - atlas.localZ) < 0.25) return m.near;
    let near = false;
    for (const s of settlementSites(seed)) {
      if (s.tier !== 'city') continue;
      if (Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ) <= HANGAR_RANGE_M) { near = true; break; }
    }
    m.x = atlas.localX; m.z = atlas.localZ; m.seed = seed; m.near = near;
    return near;
  }

  private ownedHovercraft(): Array<{ id: bigint; key: string; quality: number }> {
    const conn = atlas.conn;
    const out: Array<{ id: bigint; key: string; quality: number }> = [];
    if (!conn?.identity) return out;
    try {
      for (const it of conn.db.playerItem.iter()) {
        if (it.owner.isEqual(conn.identity) && HOVERCRAFT[it.itemKey]) {
          out.push({ id: it.itemId, key: it.itemKey, quality: it.quality });
        }
      }
    } catch { /* row cache mid-churn */ }
    // fastest machine first (speed honors quality)
    out.sort((a, b) => hovercraftSpeed(HOVERCRAFT[b.key], b.quality) - hovercraftSpeed(HOVERCRAFT[a.key], a.quality));
    return out;
  }

  // ─── hovercraft ───────────────────────────────────────────────────────────

  mountBest(itemId?: bigint): void {
    if (this.mode !== 'none' || atlas.spaceFlight || atlas.flyMode || !this.onGroundWorld()) return;
    // Aboard the sky-ship a skimmer seats at TERRAIN height 760 m below — the
    // mount would drag you straight down through the decks (probe: −11 m in
    // 2.5 s). The airlock console is the only way down.
    if (aboardSkyShip()) { this.status('No skimmers aboard the ship — take the airlock down first'); return; }
    const owned = this.ownedHovercraft();
    const pick = itemId !== undefined ? owned.find((o) => o.id === itemId) : owned[0];
    if (!pick) { this.status('You own no hovercraft — craft one at a workshop (F6)'); return; }
    this.hoverItem = pick;
    this.mode = 'hover';
    atlas.hoverRide = true;
    this.velX = this.velY = this.velZ = 0;
    this.hoverMouseSteer = 0; atlas.hoverMouseDx = 0;  // a stale look delta must not kick the new ride
    this.hoverBank = this.hoverPitch = 0;
    this.hoverAirborne = false;
    this.sendAcc = 0;
    const spec = HOVERCRAFT[pick.key];
    this.seatHover(spec.hoverM); // start AT hover height (never below ground → no spring blow-up)
    this.ensureRideMesh(`hover:${pick.key}`);
    this.status(`${spec.name} hums to life — X to step off`);
  }

  dismount(): void {
    if (this.mode !== 'hover') return;
    this.mode = 'none';
    atlas.hoverRide = false;
    // The whir ends with the ride (2026-09-23): only hoverFixed drives this loop, and the runtime holds a loop at its
    // last set() — every ride left the skimmer humming, head-locked, across worlds, until the next ride.
    sfxLoop('sfx_hover_engine', 'vehicle').stop(0.3);
    sfxLoop('sfx_hover_scrape', 'vehicle').stop(0.2); // …and a grind in progress (physics follow-up A)
    sfxLoop('sfx_water_spray', 'vehicle').stop(0.3); // …and the hiss on water (W5)
    this.wallScrape = newScrape();
    this.hoverMouseSteer = 0; atlas.hoverMouseDx = 0;
    this.hoverItem = null;
    this.clearRideMesh();
    const conn = atlas.conn;
    conn?.reducers.dismountVehicle({}).catch(() => { /* animState clears on the next walk */ });
    this.status('You step off the skimmer');
  }

  /** Seat the craft exactly at hover height above the ground at its XZ, so the
   *  spring starts at equilibrium (a mount below the terrain sample would make
   *  the spring launch it — the vy blow-up fix). */
  private seatHover(hoverM: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    const g = Math.max(groundYIn(conn, atlas.currentWorldId, atlas.localX, atlas.localZ), this.seaFloor());
    atlas.localY = g + hoverM;
    this.velY = 0;
  }

  private hoverFixed(): void {
    const conn = atlas.conn;
    const item = this.hoverItem;
    if (!conn || !item || !this.onGroundWorld()) { this.dismount(); return; }
    const spec = HOVERCRAFT[item.key];
    const top = hovercraftSpeed(spec, item.quality);
    const boostTop = hovercraftBoostSpeed(spec, item.quality); // per-chassis SHIFT ceiling (~80-120)
    const sea = this.seaFloor();

    // ── Racing controls (the vehicle module owns its own key set) ──
    // Mouse steers the heading (localYaw) as before; W/S = throttle+brake/reverse,
    // A/D = steering assist, Space = handbrake (drift), Shift = boost.
    let throttle = (keyHeldIn(this.keys, 'throttleUp') ? 1 : 0) - (keyHeldIn(this.keys, 'throttleDown') ? 1 : 0);
    // A / ArrowLeft steer LEFT, D / ArrowRight steer RIGHT (the conventional way).
    const steer = (keyHeldIn(this.keys, 'steerLeft') ? 1 : 0) - (keyHeldIn(this.keys, 'steerRight') ? 1 : 0);
    const handbrakeKey = keyHeldIn(this.keys, 'handbrake');
    // ── GAMEPAD (2026-09-16): analog throttle + analog steering, blended PER
    //    CHANNEL so a pad left idle in a USB port never fights the keyboard and
    //    a player can use both at once. The larger steering lock wins, exactly
    //    as it does between the keys and the mouse. ──
    // The SHARED poll (lib/gamepad.ts, Input phase) already read the pad this
    // frame and measured its profile — the fixed tick can run several times per
    // frame, so re-reading here would call getGamepads() up to five times and
    // give the ride a different frame than the rest of the game.
    // In a headset the snapshot IS the controllers (the XR module feeds it; no desktop profile) — XR P2.16.
    const pr = padXrActive() ? padRideFromSnapshot(padSnap(), comfort().moveHand) : padRide(padCached(), padSnap().profile);
    this.padLast = pr;
    if (pr.throttle !== 0) throttle = pr.throttle;
    const handbrake = handbrakeKey || pr.handbrake;
    const boosting = (keyHeldIn(this.keys, 'boost') || pr.boost) && this.boosterE > 0 && throttle > 0;
    // Booster reservoir (shared with flight — you can only ride one thing).
    this.boosterE = boosting
      ? Math.max(0, this.boosterE - BOOSTER_DRAIN * FIXED_DT)
      : Math.min(BOOSTER_MAX, this.boosterE + BOOSTER_REGEN * FIXED_DT);

    // ── Advance the pure arcade integrator (momentum, drag→top, grip/drift,
    //    hover spring vs gravity = ramp jumps). Y is client-side (the server
    //    vehicleMove carries only XZ+yaw). ──
    const st: HoverState = { x: atlas.localX, z: atlas.localZ, y: atlas.localY, vx: this.velX, vy: this.velY, vz: this.velZ, yaw: atlas.localYaw };
    // The mouse's banked look delta becomes a persistent STEERING AXIS that the
    // ordinary model honours, combined with the keys (the larger lock wins, so a
    // key and the mouse never fight). Consumed and zeroed every fixed tick.
    const mdx = atlas.hoverMouseDx;
    atlas.hoverMouseDx = 0;
    this.hoverMouseSteer = mouseSteerStep(this.hoverMouseSteer, mdx, FIXED_DT);
    let steerIn = Math.abs(this.hoverMouseSteer) > Math.abs(steer) ? this.hoverMouseSteer : steer;
    if (Math.abs(pr.steer) > Math.abs(steerIn)) steerIn = pr.steer;   // the stick, if it is asking for more
    const inp: HoverInput = { throttle, steer: steerIn, handbrake, boost: boosting };
    const fluid = activePreset().fluidPhysics !== false;
    // The STILL water level over (x, z): the sea, a lake or a river when the
    // water provider is up (water_fx answers all three), the sea alone
    // otherwise. WHY: the sea level was the only water the hull knew — over a
    // lake at +30 m it seated on the LAKEBED under the surface (2026-09-12).
    const stillAt = (x: number, z: number, g: number): number | null => {
      const w = waterStillAt(x, z);
      if (w !== null) return g < w - 0.2 ? w : null;
      return g < sea - 0.2 ? sea : null;
    };
    const groundAt = (x: number, z: number): number => {
      const g = groundYIn(conn, atlas.currentWorldId, x, z);
      const w = stillAt(x, z, g);
      if (w === null) return Math.max(g, sea);
      // Interactive water: over open water the hull rides the LIVE surface
      // (the swell, its own wake) instead of the still level.
      if (fluid) { const live = waterLiveAt(x, z); if (live !== null) return Math.max(g, live); }
      return Math.max(g, w);
    };
    // Open water = the terrain lies under a water surface (never true off the
    // Commons, where seaFloor() is -1e9 and no provider answers): the hull
    // rides a wake and slows to 30.
    const onWater = (x: number, z: number): boolean => stillAt(x, z, groundYIn(conn, atlas.currentWorldId, x, z)) !== null;
    // Structures (buildings, incl. the whole city skyline) are SOLID to a
    // hovercraft — the same solids registry the ship collides against. HOVER_BLOCK
    // is a small skimmer-width margin so you bounce just before the visible wall.
    //    The hull is probed at nose / centre / tail — see `hoverBlocked`; on the
    //    ground (agl = the candidate y over the hull's own ground) the wall MASK answers first.
    //    THE SEAT: a seated craft's hull is drawn ahead of / behind its rider (`hullShift`), and a high seat must not lift
    //    the wall test (`seatLift`). Both are exactly 0 for a craft with no seat row at a founding ride height.
    const seatRow = hoverSeat(item.key);
    const hullShift = seatRow ? seatHullShift(seatRow) : 0, seatLift = Math.max(0, spec.hoverM - HOVER_SEAT_BAND_M);
    const blocked = (x: number, z: number, y: number, yaw: number): ReturnType<typeof hoverBlocked> => hoverBlocked(x, z, y - seatLift, yaw, y - seatLift - groundAt(x, z), hullShift);
    const r = stepHover(st, inp, groundAt, { top, boostTop, accel: spec.accel, hoverM: spec.hoverM }, FIXED_DT, blocked, onWater);
    atlas.localX = st.x; atlas.localY = st.y; atlas.localZ = st.z;
    atlas.localYaw = st.yaw;
    this.velX = st.vx; this.velY = st.vy; this.velZ = st.vz;
    this.hoverBank = r.bank; this.hoverPitch = r.pitch; this.hoverAirborne = r.airborne; this.hoverGap = r.gap;
    this.rideOnWater(r, fluid);

    // ── SFX: engine whir rides real speed (normalized to the BOOST ceiling so a
    //    surge is audible) + a boost edge + a drift mini-turbo whoosh + a thump ──
    const spN = Math.min(1, r.speed / Math.max(1, boostTop));
    sfxLoop('sfx_hover_engine', 'vehicle').set(0.06 + spN * (boosting ? 0.42 : 0.3), 0.85 + spN * (boosting ? 0.6 : 0.35));
    // W5 (2026-10-05): the cushion on WATER hisses — an idle hiss at rest, a rush with speed (every tier: audio is not
    // the GPU's cost). Head-locked (YOUR craft); the 'vehicle' scope's loop is stopped with the ride.
    const spray = hoverSprayMix(r.onWater, r.speed, this.sprayMix);
    sfxLoop('sfx_water_spray', 'vehicle').set(spray.gain, spray.rate, 0.25);
    waterFx().interact.sprayGain = spray.gain;
    if (boosting && !this.wasBoosting && this.boosterE > 8) sfx('sfx_ship_boost', { gain: 0.4, cooldownMs: 900 });
    this.wasBoosting = boosting;
    if (r.turbo > 0 && this.hoverTurboPrev <= 0) sfx('sfx_ship_boost', { gain: 0.5, cooldownMs: 300 }); // drift mini-turbo fires
    this.hoverTurboPrev = r.turbo;
    this.hoverDrifting = r.drifting; this.hoverDriftCharge = r.driftCharge;
    this.hoverNatural = r.natural; this.hoverOnWater = r.onWater;
    if (r.landed > 18) { sfx('sfx_ship_land', { gain: 0.35, cooldownMs: 500 }); padRumble('land', Math.min(1, r.landed / 40)); cameraImpulse(-0.022 * Math.min(1, r.landed / 40), 0); } // a hard touchdown dips the chase (scaled by the camera-motion row)
    // THE WALL, through the pad. The readout already separates the two: `impact`
    // is non-zero only on the tick contact BEGINS and carries the closing speed,
    // while `contact` stays true through the grind — so a hit thumps once and a
    // scrape merely rasps, which is the same split the physics makes.
    if (r.impact > 0) padRumble('crash', Math.min(1, r.impact / (60 * VEHICLE_SPEED_SCALE)));
    else if (r.contact) padRumble('scrape', Math.min(1, Math.hypot(this.velX, this.velZ) / Math.max(1, top)));
    // THE WALL, HEARD (physics follow-up A, 2026-10-05): the same split, through the speakers and the camera — a thunk on
    // the rising edge (log gain by the closing speed, pitch by the hull's mass, an 80 ms cooldown on the FIXED clock) and
    // a grind loop while the contact holds (gain + rate by the speed along the wall, released when it ends).
    if (r.impact > 0) this.wallImpact(r.impact, HOVER_MASS_T[item.key] ?? WALL_MASS_REF_T);
    const grind = scrapeStep(this.wallScrape, r.contact, r.along, FIXED_DT); // the speed ALONG the wall (the readout's `along`, the bounce excluded)
    sfxLoop('sfx_hover_scrape', 'vehicle').set(grind.gain, grind.rate, grind.ramp);

    // ── 5 Hz authoritative stream — row-anchored + step-limited (the 2026-08-20
    //    cascade fix). The cap tracks the server's 1.5× catalog-speed budget so a
    //    boosted skimmer streams smoothly instead of rubber-banding. ──
    this.sendAcc++;
    const row = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    if (this.sendAcc >= SEND_EVERY_TICKS && row && row.worldId === atlas.currentWorldId) {
      this.sendAcc = 0;
      let dx = atlas.localX - row.posX, dz = atlas.localZ - row.posZ;
      const dLen = Math.hypot(dx, dz);
      const stepMax = boostTop * 0.2 + 3; // < server boost cap (boostSpeed·1.2·0.2 s + slack)
      if (dLen > stepMax) { const sc = stepMax / dLen; dx *= sc; dz *= sc; }
      // The HEIGHT rides along (2026-09-16). It is anchored and step-limited the
      // same way the horizontal delta is, against the server's own vertical
      // budget, so a ramp launch streams instead of bouncing off the validator.
      let dy = atlas.localY - row.posY;
      const yMax = HOVER_MAX_VY * 0.2 + 3;            // < server vCap (45·1.2·0.2 s + slack)
      if (Math.abs(dy) > yMax) dy = Math.sign(dy) * yMax;
      conn.reducers.vehicleMove({ itemId: item.id, posX: row.posX + dx, posY: row.posY + dy, posZ: row.posZ + dz, yaw: atlas.localYaw })
        .catch(() => { /* a jittered write bounced — the next send re-anchors to the row */ });
    }
  }

  // ─── ship flight ──────────────────────────────────────────────────────────

  async board(): Promise<void> {
    if (this.mode !== 'none' || atlas.spaceFlight || atlas.flyMode || !this.onGroundWorld()) return;
    if (aboardSkyShip()) { this.status('Your ship is berthed far below — take the airlock down first'); return; }
    const conn = atlas.conn;
    if (!conn) return;
    // Stand the walking stream down BEFORE the round-trip: a 5 Hz walk send
    // racing boardShip clobbered the fresh fly: row ('walk' 23 ms after
    // liftoff — the frozen-row / no-ascent field bug, 2026-08-23). The
    // server also refuses walking packets on fly: rows now; this closes the
    // client's own window. Reverted on refusal.
    atlas.skyFlight = true;
    try {
      await conn.reducers.boardShip({});
    } catch (e) {
      atlas.skyFlight = false;
      this.status(`Takeoff refused: ${(e as Error).message ?? e}`);
      return;
    }
    sfx('sfx_ship_takeoff', { gain: 0.7, cooldownMs: 1500 });
    // The berth row died server-side, but the row-DELETE replicates on its
    // own schedule and syncBerths only scans at 0.5 Hz — drop OUR parked
    // hull instantly so takeoff never shows a ghost ship on the pad below
    // (user report 2026-08-23).
    const hex = conn.identity?.toHexString();
    if (hex) {
      const parked = this.berthMeshes.get(hex);
      if (parked) {
        this.app?.world.scene.remove(parked.obj);
        deferDisposeTree(parked.obj);
        this.berthMeshes.delete(hex);
      }
    }
    this.enterFlight();
  }

  /** Flip the client into flight mode (fresh takeoff OR mid-flight resume). */
  private enterFlight(): void {
    const conn = atlas.conn;
    const me = conn?.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    this.flyChassis = me?.animState.startsWith('fly:') ? me.animState.slice(4) : 'ship_lancer';
    this.mode = 'fly';
    atlas.skyFlight = true;
    this.velX = this.velY = this.velZ = 0;
    this.sendAcc = 0;
    this.flyCamBack = chaseFor(CHASSIS[this.flyChassis]); // start at the hull's chase distance (a long hull's is its own)
    this.ascending = false;
    this.crashing = false;
    this.refreshShipStats();
    this.hull = this.stats.hp;
    this.shield = this.stats.shield;
    this.cap = this.stats.capacitor;
    this.boosterE = BOOSTER_MAX;
    this.throttle = 0.35;
    this.cruise = 1;
    this.flyQuat.setFromEuler(new THREE.Euler(0, atlas.localYaw, 0, 'YXZ'));
    if (me) atlas.localY = Math.max(atlas.localY, me.posY);
    this.ensureRideMesh(`fly:${this.flyChassis}`);
    this.status('Airborne — W/S throttle, mouse steers the nose, A/D roll, R levels, SHIFT boost, SPACE/click fires, X lands');
  }

  async land(): Promise<void> {
    if (this.mode !== 'fly') return;
    const conn = atlas.conn;
    if (!conn) return;
    const ground = groundYIn(conn, atlas.currentWorldId, atlas.localX, atlas.localZ);
    if (atlas.localY - ground > LAND_MAX_AGL) { this.status('Too high to set down — descend first'); return; }
    const blocker = this.landBlocker(atlas.localX, atlas.localZ);
    if (blocker) {
      this.status(blocker === 'water' ? 'Open water takes no landing gear' : `No landing inside a ${blocker} — clear its airspace`);
      return;
    }
    try {
      await conn.reducers.landShip({});
    } catch (e) {
      this.status(`Landing refused: ${(e as Error).message ?? e}`);
      return;
    }
    this.exitFlight();
    atlas.localY = ground;
    sfx('sfx_ship_land', { gain: 0.7, cooldownMs: 1500 });
    this.status('Touchdown. Your ship holds this ground until you board or recall it');
  }

  private exitFlight(): void {
    sfxLoop('sfx_ship_engine', 'vehicle').stop();
    sfxLoop('sfx_hull_alarm', 'vehicle').stop();
    sfxLoop('sfx_water_downwash', 'vehicle').stop(0.4); // W2: the roar over water ends with the flight
    this.skimming = false; this.breachArmed = true; this.washDebt.debt = 0; this.washDiag.over = false; this.washDiag.k = 0;
    this.clearBolts();
    this.mode = 'none';
    atlas.skyFlight = false;
    this.flyChassis = '';
    this.ascending = false;
    if (this.cockpitView) { this.cockpitView = false; this.cockpitOverlay.hide(); }
    this.cockpitOverlay.dispose();
    this.clearRideMesh();
  }

  async recall(): Promise<void> {
    if (this.mode === 'fly' || atlas.spaceFlight || !this.inCommons()) return;
    const conn = atlas.conn;
    if (!conn) return;
    try {
      await conn.reducers.recallShip({});
      this.status('Your ship answers — it sets down beside you');
      this.berthAcc = 99; // re-scan berth meshes now
    } catch (e) {
      this.status(`Recall refused: ${(e as Error).message ?? e}`);
    }
  }

  private flyFixed(): void {
    const conn = atlas.conn;
    if (!conn || !this.onGroundWorld()) { if (!this.ascending) this.exitFlight(); return; }
    const dt = FIXED_DT;

    // ── GAMEPAD (2026-09-16): a spacecraft's conventions, not a racer's — the
    //    left stick is the throttle and the roll, the right stick flies the
    //    nose, the triggers are guns and burner. Blended with the keys so
    //    either drives. ──
    const pad = padSnap();
    // Held throttle (JTL: W/S set it; the ship keeps its speed).
    const padThr = pad.present ? -pad.ly : 0;
    const kUp = keyHeldIn(this.keys, 'throttleUp'), kDn = keyHeldIn(this.keys, 'throttleDown');
    if (kUp || padThr > 0.15) this.throttle = Math.min(1, this.throttle + THROTTLE_RATE * dt * Math.max(padThr, kUp ? 1 : 0));
    if (kDn || padThr < -0.15) this.throttle = Math.max(0, this.throttle - THROTTLE_RATE * dt * Math.max(-padThr, kDn ? 1 : 0));

    // Roll A/D (and Q/E), the pad's shoulders or its left stick; R (or A) levels
    // the wings — exactly the sector's stick.
    let rollIn = (keyHeldIn(this.keys, 'rollLeft') ? 1 : 0) - (keyHeldIn(this.keys, 'rollRight') ? 1 : 0);
    if (rollIn === 0 && pad.present) {
      rollIn = (padHeld('rollLeft') ? 1 : 0) - (padHeld('rollRight') ? 1 : 0);
      if (rollIn === 0 && Math.abs(pad.lx) > 0.15) rollIn = -pad.lx;
    }
    if (rollIn !== 0) this.flyQuat.multiply(this._q1.setFromAxisAngle(this._v1.set(0, 0, 1), rollIn * ROLL_RATE * dt));
    // The right stick flies the nose, where the pointer-locked mouse otherwise
    // does. The input module leaves the look alone while skyFlight is set, so
    // there is no double-steer.
    if (pad.present) {
      const look = padLook(dt);
      if (look.dx !== 0 || look.dy !== 0) {
        this.flyQuat.multiply(this._q1.setFromAxisAngle(this._v1.set(0, 1, 0), -look.dx));
        this.flyQuat.multiply(this._q1.setFromAxisAngle(this._v1.set(1, 0, 0), -look.dy));
        this.steerRate = Math.min(1.6, (Math.abs(look.dx) + Math.abs(look.dy)) * 26);
      }
    }
    if (keyHeldIn(this.keys, 'levelOut') || padHeld('levelOut')) {
      const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.flyQuat);
      if (Math.abs(fwd.y) < 0.985) {
        this._m1.lookAt(new THREE.Vector3(), fwd, new THREE.Vector3(0, 1, 0));
        this.flyQuat.slerp(this._q1.setFromRotationMatrix(this._m1), Math.min(1, LEVEL_RATE * dt));
      }
    }

    // Booster: SHIFT burns the reservoir; it refills off-boost.
    const boosting = (keyHeldIn(this.keys, 'boost') || padHeld('boost')) && this.boosterE > 0;
    this.boosterE = boosting
      ? Math.max(0, this.boosterE - BOOSTER_DRAIN * dt)
      : Math.min(BOOSTER_MAX, this.boosterE + BOOSTER_REGEN * dt);

    // Overcruise: steady full throttle winds toward ×10; steering/boost spool it down.
    if (this.throttle > 0.98 && this.steerRate < 0.3 && !boosting) {
      this.cruise = Math.min(CRUISE_MAX, this.cruise + CRUISE_RAMP * dt);
    } else {
      this.cruise = Math.max(1, this.cruise - CRUISE_DECAY * dt);
    }
    this.steerRate = Math.max(0, this.steerRate - dt * 5);

    // Velocity chases the nose (engine inertia — the ship flies where it points).
    // VEHICLE_SPEED_SCALE: atmospheric ship flight is a vehicle too (2026-09-02).
    const speedCap = Math.min(FLY_MAX_MPS, this.stats.maxSpeed * Math.max(boosting ? BOOST_MULT * this.stats.boost : 1, this.cruise)) * VEHICLE_SPEED_SCALE;
    const desired = this._v1.set(0, 0, -1).applyQuaternion(this.flyQuat).multiplyScalar(this.throttle * speedCap);
    const k = Math.min(1, ACCEL_K * dt);
    this.velX += (desired.x - this.velX) * k;
    this.velY += (desired.y - this.velY) * k;
    this.velZ += (desired.z - this.velZ) * k;
    const nx = atlas.localX + this.velX * dt;
    const nz = atlas.localZ + this.velZ * dt;
    let ny = atlas.localY + this.velY * dt;
    const speed = Math.hypot(this.velX, this.velY, this.velZ);

    // ── TERRAIN: the ground is real — arrive gently or pay for it ──────────
    const ground = groundYIn(conn, atlas.currentWorldId, nx, nz);
    if (ny < ground + 2.5) {
      const impact = Math.max(Math.abs(this.velY), speed * 0.55);
      ny = ground + 2.5;
      if (impact > IMPACT_SAFE_MPS) {
        this.damage((impact - IMPACT_SAFE_MPS) * 1.8, 'terrain impact');
        this.velX *= 0.25; this.velY = Math.abs(this.velY) * 0.2; this.velZ *= 0.25;
        this.throttle = Math.min(this.throttle, 0.2);
      } else {
        this.velY = Math.max(0, this.velY);
      }
    }
    // ── WATER (W2, 2026-10-05): the ship SKIMS the live surface, never flies under it ──
    // The terrain clamp above holds the ship 2.5 m over the BED, so over a lake or the sea a pilot could fly along the
    // sea floor with the camera under the water. The live surface is a floor too: the belly meets it SHIP_SKIM_M under
    // the centre and skims (water is soft only when met flat — the impact is the VERTICAL closing speed), shedding speed
    // while it is in. Downwash below: spray and rings under the hull, falling with height, rising with drive.
    ny = this.flyOverWater(nx, nz, ny, ground, dt);
    // ── STRUCTURES: settlement buildings are SOLID at ANY altitude ─────────
    // The old test gated on <40 m AGL and a height-blind 2-D footprint, so a
    // ship flew straight through the tall city towers. collideStructure tests the
    // building's REAL built volume (ground → roof), so hitting a 300 m tower at
    // cruise altitude bounces the ship out of the footprint, damages shield-
    // then-hull scaled by impact speed, and throws an impact explosion.
    const out = this.collideStructure(nx, nz, ny, speed);
    if (out) { atlas.localX = out.x; atlas.localZ = out.z; }
    else { atlas.localX = nx; atlas.localZ = nz; }
    atlas.localY = ny;

    // The cloud deck thickens through [CLOUD_BASE_Y, CLOUD_FULL_Y] — by the
    // ASCEND_Y handoff the sky is pure cloud and the world swap is invisible.
    cloudTunnel.setAmbient(Math.min(1, Math.max(0, (atlas.localY - CLOUD_BASE_Y) / (CLOUD_FULL_Y - CLOUD_BASE_Y))));

    // Engine hum rides the throttle (pitch follows real speed); the boost
    // edge gets its own ignition whoosh.
    const spdNorm = Math.min(1, Math.hypot(this.velX, this.velY, this.velZ) / Math.max(1, this.stats.maxSpeed * 2.5));
    sfxLoop('sfx_ship_engine', 'vehicle').set(0.09 + this.throttle * 0.28, 0.85 + spdNorm * 0.5);
    if (boosting && !this.wasBoosting && this.boosterE > 8) sfx('sfx_ship_boost', { gain: 0.55, cooldownMs: 1200 });
    this.wasBoosting = boosting;

    // Hull-critical cockpit alarm (loop, gated under 25%).
    sfxLoop('sfx_hull_alarm', 'vehicle').set(this.stats.hp > 0 && this.hull > 0 && this.hull / this.stats.hp < 0.25 ? 0.22 : 0, 1, 0.3);
    // Capacitor + shield regen (5 quiet seconds), cannon cooldown.
    this.fireCool = Math.max(0, this.fireCool - dt);
    this.cap = Math.min(this.stats.capacitor, this.cap + CAP_REGEN * this.stats.regenMult * dt);
    if (performance.now() - this.lastHitT > 5000) {
      this.shield = Math.min(this.stats.shield, this.shield + 2.5 * this.stats.regenMult * dt);
    }
    if (keyHeldIn(this.keys, 'fire') || padHeld('fire')) this.fire();
    this.stepBolts(dt);

    // Stats refresh (a refit at the dock lands mid-flight too).
    this.statsAcc += dt;
    if (this.statsAcc >= 5) { this.statsAcc = 0; this.refreshShipStats(); }

    // 5 Hz row-anchored stream — the step budget scales with the real speed
    // (an overcruising Talon covers ~260 m per send; the server allows 1500·dt).
    this.sendAcc++;
    const row = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    if (this.sendAcc >= SEND_EVERY_TICKS && row && row.worldId === atlas.currentWorldId) {
      this.sendAcc = 0;
      let dx = atlas.localX - row.posX, dy = atlas.localY - row.posY, dz = atlas.localZ - row.posZ;
      const dLen = Math.hypot(dx, dy, dz);
      const stepMax = Math.min(280, speed * 0.25 + 30);
      if (dLen > stepMax) { const sc = stepMax / dLen; dx *= sc; dy *= sc; dz *= sc; }
      const e = new THREE.Euler().setFromQuaternion(this.flyQuat, 'YXZ');
      atlas.localYaw = e.y;
      conn.reducers.flyMove({ posX: row.posX + dx, posY: row.posY + dy, posZ: row.posZ + dz, yaw: e.y })
        .catch(() => { /* a jittered write bounced — the next send re-anchors */ });
    }
    // The ascent band: judged on the AUTHORITATIVE altitude. ONLY the Commons sky
    // opens to space — a personal / spawned world's atmosphere just hits the
    // FLY_CEILING clamp (the server refuses ascent off-Commons regardless).
    if (this.inCommons() && (row?.posY ?? 0) >= ASCEND_Y && !this.ascending) {
      this.ascending = true;
      void this.ascend();
    }
  }

  /** Damage routes shield-first (the space contract); a dead hull crashes. */
  /** A hit TAKEN marks the hull (2026-09-08): a scorch on a random point of
   *  the ride mesh's bounds, facing out, riding the mesh. */
  private hullScorch(amount: number, hull: THREE.Object3D | null = this.rideMesh): THREE.Mesh | null {
    const scene = this.app?.world.scene;
    if (!hull || !scene || amount <= 0) return null;
    const box = new THREE.Box3().setFromObject(hull);
    if (box.isEmpty()) return null;
    const c = box.getCenter(new THREE.Vector3()), reach = box.getSize(new THREE.Vector3()).length();
    // ON THE MESH (2026-09-08, "the decals were floating around my ship"): the
    // old mark sat on the bounding-box shell, which a winged hull fills a
    // fifth of. Now a ray from outside, along a random direction biased
    // upward and sideways (where the blows come from), finds the hull's own
    // surface; a miss (the ray passed the silhouette) tries again, and with no
    // surface at all there is no mark — never a floater.
    for (let i = 0; i < 6; i++) {
      const a = Math.random() * Math.PI * 2, up = 0.15 + Math.random() * 0.75;
      const d = new THREE.Vector3(Math.cos(a) * (1 - up), up, Math.sin(a) * (1 - up)).normalize();
      const from = new THREE.Vector3(c.x + d.x * reach, c.y + d.y * reach, c.z + d.z * reach);
      const hit = surfacePointOn(hull, from, c, reach * 2);
      if (!hit) continue;
      const m = impactDecals(scene).place('scorch', hit.point, hit.normal, { parent: hull, sizeMul: 1.2 + Math.min(2, amount / 12) });
      { // shader gaps R2: the hit TAKEN strips the hull's paint where it landed; the mark rides the hull (a mover receiver)
        const a = renderToAbsolute(hit.point.clone());
        reportDamageHit({ x: a.x, y: a.y, z: a.z, nx: hit.normal.x, ny: hit.normal.y, nz: hit.normal.z, weapon: 'energy', punch: 1.2 + Math.min(2, amount / 12), mover: { id: hull === this.rideMesh ? `ride:${this.rideTag}` : `hull:${hull.uuid}`, root: hull } });
      }
      (m.userData as { surface?: number[] }).surface = [hit.point.x, hit.point.y, hit.point.z]; // the mesh point it sits on (probe evidence)
      return m;
    }
    return null;
  }

  /** Probe lever: a hull (the ride, else a built fallback at the player) takes
   *  `n` scorches; every mark is then re-measured against the hull's own
   *  surface (a ray through the mark along its normal) — the distance from the
   *  mark to the mesh must be the DECAL_LIFT_M and nothing more. */
  private hullScorchTest(n: number): { placed: number; maxOffM: number; meanOffM: number; hull: string; offs: number[] } {
    const scene = this.app?.world.scene;
    if (!scene) return { placed: 0, maxOffM: -1, meanOffM: -1, hull: 'no-scene', offs: [] };
    let hull = this.rideMesh, temp = false;
    if (!hull) {
      hull = this.buildVehicle(`ship:${this.flyChassis || DEFAULT_CHASSIS}`);
      hull.position.set(atlas.localX, atlas.localY + 6, atlas.localZ);
      scene.add(hull);
      hull.updateMatrixWorld(true);
      temp = true;
    }
    const marks: THREE.Mesh[] = [];
    for (let i = 0; i < n; i++) { const m = this.hullScorch(10, hull); if (m) marks.push(m); }
    hull.updateMatrixWorld(true);
    const offs: number[] = [];
    const p = new THREE.Vector3(), q = new THREE.Quaternion(), nrm = new THREE.Vector3();
    // The evidence: each mark's distance from the MESH POINT its placement ray
    // hit (a raycast hit is on the surface by construction) — exactly the lift.
    // (A second raycast back through the mark is not a fair measure: three's
    // raycaster honours single-sided materials and thin panels, and a dozen
    // neighbouring quads sit 3 cm proud of the same hull.)
    const sp = new THREE.Vector3();
    for (const m of marks) {
      m.getWorldPosition(p); m.getWorldQuaternion(q); nrm.set(0, 0, 1).applyQuaternion(q);
      const s = (m.userData as { surface?: number[] }).surface;
      if (!s) { offs.push(9); continue; }
      sp.set(s[0], s[1], s[2]);
      offs.push(sp.distanceTo(p));
    }
    if (temp) { impactDecalsIfAny()?.ageAll(1e6); impactDecalsIfAny()?.update(0); scene.remove(hull); }
    const maxOff = offs.length ? Math.max(...offs) : -1, mean = offs.length ? offs.reduce((a, b) => a + b, 0) / offs.length : -1;
    return { placed: marks.length, maxOffM: +maxOff.toFixed(3), meanOffM: +mean.toFixed(3), hull: temp ? 'built-fallback' : 'ride', offs: offs.map((o) => +o.toFixed(3)) };
  }

  private damage(amount: number, why: string): void {
    if (this.crashing) return;
    this.lastHitT = performance.now();
    this.hullScorch(amount);
    if (this.rideShield) { this.shieldHitAt = performance.now(); stage3Stats.shieldHits++; } // stage 3: the shell flashes
    const absorbed = Math.min(this.shield, amount);
    this.shield -= absorbed;
    this.hull -= amount - absorbed;
    sfx(amount - absorbed > 0 ? 'sfx_ship_impact' : 'sfx_shield_hit', { gain: 0.65, cooldownMs: 250 });
    if (amount - absorbed > 1) this.status(`⚠ ${why} — hull ${Math.max(0, Math.round(this.hull))}/${this.stats.hp}`);
    if (this.hull <= 0) {
      this.crashing = true;
      sfx('sfx_ship_crash', { gain: 0.9, cooldownMs: 2000 });
      this.status('HULL BREACHED — going down');
      void this.crash();
    }
  }

  private async crash(): Promise<void> {
    const conn = atlas.conn;
    if (!conn) { this.crashing = false; return; }
    try {
      await conn.reducers.crashShip({});
    } catch { /* not flying any more — the row settles it */ }
    this.exitFlight();
    this.crashing = false;
    this.status('You walk away from the wreck — the ship sits where it came down (recovery fee charged)');
  }

  /** Twin cannons: capacitor-fed bolts that strike wilds creatures (server-validated). */
  private fire(): void {
    if (this.fireCool > 0 || this.cap < CAP_VOLLEY || !this.app) return;
    this.cruise = Math.max(1, this.cruise * 0.5); // guns online = engines throttle back
    this.fireCool = FIRE_COOLDOWN_S * this.stats.fireCdMult;
    this.cap -= CAP_VOLLEY;
    sfx('sfx_ship_cannon', { gain: 0.42, cooldownMs: 90 });
    const fwd = this._v1.set(0, 0, -1).applyQuaternion(this.flyQuat).clone();
    for (const side of [-1.6, 1.6]) {
      const muzzle = new THREE.Vector3(side, -0.4, -3).applyQuaternion(this.flyQuat);
      // Pooled sprite on the ONE shared textured material (see boltMaterial).
      const b = this.boltPool.pop() ?? new THREE.Sprite(boltMaterial());
      b.visible = true;
      b.scale.setScalar(3.2);
      b.position.set(atlas.localX, atlas.localY, atlas.localZ).add(muzzle);
      if (!b.parent) this.app.world.scene.add(b);
      this.ensureVfx()?.muzzle(b.position); // a spark leaves each cannon
      this.bolts.push({ sprite: b, vel: fwd.clone().multiplyScalar(BOLT_SPEED).add(new THREE.Vector3(this.velX, this.velY, this.velZ)), ttl: BOLT_LIFE_S });
    }
  }

  /**
   * W2: the ship over water, once per fixed tick — the skim floor, the breach splash (one per meeting of hull and
   * water, re-armed when the hull lifts a metre clear), the downwash (spray particles at a turning point on a ring under
   * the hull, the particle budget's gate; a ring stamp at most every DOWNWASH_RING_EVERY_S, the fluid flag's gate) and
   * its head-locked roar. Returns the (possibly floored) height. ABSOLUTE coordinates throughout.
   */
  private flyOverWater(x: number, z: number, y: number, ground: number, dt: number): number {
    const still = waterStillAt(x, z);
    const hullM = CHASSIS[this.flyChassis]?.hullSize ?? 12;
    this.washDiag.hullM = hullM;
    if (still === null || still < ground + 0.3) {
      sfxLoop('sfx_water_downwash', 'vehicle').set(0, 1, 0.4);
      this.washDebt.debt = 0; this.skimming = false; this.breachArmed = true;
      this.washDiag.over = false; this.washDiag.k = 0; this.washDiag.gain = 0; this.washDiag.heightM = -1; this.washDiag.skimming = false;
      return y;
    }
    const live = waterLiveAt(x, z) ?? still;
    const floor = live + SHIP_SKIM_M;
    const vyIn = this.velY;
    const speedH = Math.hypot(this.velX, this.velZ);
    let out = y;
    const touching = out <= floor + 0.05;
    if (out < floor) {
      out = floor;
      const impact = skimImpactMps(vyIn);
      if (impact > IMPACT_SAFE_MPS) {
        this.damage((impact - IMPACT_SAFE_MPS) * 1.4, 'water impact');
        this.throttle = Math.min(this.throttle, 0.3);
      }
      if (this.velY < 0) this.velY = 0;
      const kd = Math.max(0, 1 - SKIM_DRAG_PER_S * dt);
      this.velX *= kd; this.velZ *= kd;
    }
    const ia = waterFx().interact;
    if (touching && this.breachArmed) {
      // ONE BREACH, ONE SPLASH (the Niagara breach anchor): fire on the crossing, not every tick.
      this.breachArmed = false;
      const sp = breachSplash(Math.max(0, -vyIn), speedH, hullM);
      splashWater(x, live, z, sp.count, sp.speed);
      stampWater(x, z, sp.stamp.strength, sp.stamp.radius);
      sfx('sfx_water_splash', { gain: Math.min(0.85, 0.35 + sp.count / 180), cooldownMs: 400 });
      ia.breaches++;
    } else if (!this.breachArmed && out > floor + 1) this.breachArmed = true;
    this.skimming = touching;
    const heightM = Math.max(0, out - floor);
    const thrust = Math.min(1, this.throttle * (this.wasBoosting ? 1.4 : 1));
    const dw = downwashFor(heightM, hullM, Math.hypot(speedH, this.velY), thrust, this.washOut);
    this.washDiag.over = true; this.washDiag.heightM = heightM; this.washDiag.k = dw.k; this.washDiag.skimming = touching; this.washDiag.gain = dw.gain;
    sfxLoop('sfx_water_downwash', 'vehicle').set(dw.gain, dw.rate, 0.35);
    if (dw.k <= 0) { this.washDebt.debt = 0; return out; }
    ia.downwash++;
    const fx = waterFx();
    const n = sprayThisTick(dw.sprayPerS * fx.particleScale, dt, this.washDebt);
    if (n > 0) {
      this.washAngle += 2.39996; // the golden angle: successive puffs walk the ring evenly
      const sx = x + Math.cos(this.washAngle) * dw.sprayRadius, sz = z + Math.sin(this.washAngle) * dw.sprayRadius;
      const sy = waterLiveAt(sx, sz) ?? live;
      if (splashWater(sx, sy, sz, n, 3)) ia.downwashSpray += n;
    }
    if (this.washGate.due('local', this.wallSimMs / 1000, DOWNWASH_RING_EVERY_S) && stampWater(x, z, dw.ring.strength, dw.ring.radius)) ia.downwashRings++;
    return out;
  }

  /** W1: a ship-cannon bolt meeting the water — a column of spray, a wide ring, a hiss and a deep plop; no explosion
   *  on the bed, no decal, no gouge. Heard at the speed of sound (a report, like the explosion it replaces). */
  private boltIntoWater(x: number, y: number, z: number): void {
    const w = waterShotFor('cannon');
    splashWater(x, y, z, w.count, w.speed);
    stampWater(x, z, w.stamp.strength, w.stamp.radius);
    this.ensureVfx()?.impact(this._hit.set(x, y + 0.8, z), 0.9, 0xe4eef4); // the steam of a bolt quenched
    const d = Math.hypot(x - atlas.localX, y - atlas.localY, z - atlas.localZ);
    const g = inverseDistanceGain(d, WATER_SHOT_REF_M * 2, WATER_SHOT_MAX_M * 2);
    const at = { x, y, z };
    const delayS = explosionSoundDelayS(at);
    if (g > 0.004) {
      sfxAt(w.key, at, { gain: w.gain * g, rate: w.rate, cooldownMs: 90, delayS, caption: 'Splash' });
      sfxAt('sfx_water_plop', at, { gain: 0.6 * g, rate: 0.62, cooldownMs: 90, delayS });
    }
    waterFx().interact.cannon++;
  }

  /** A craft struck a wall at `closingMps` (physics follow-up A): the thunk (gated 80 ms on the fixed clock), and the feel
   *  layer's kick + trauma — the camera nods, the opt-in shake rattles; both scaled by the camera-motion row. */
  private wallImpact(closingMps: number, massT: number): void {
    if (!impactGateOpen(this.wallGate, this.wallSimMs)) return;
    const snd = wallImpactSound(closingMps, massT, hitJitter(this.wallGate.played));
    if (snd) sfx('sfx_hover_impact', { gain: snd.gain, rate: snd.rate });
    const fe = wallImpactFeel(closingMps);
    if (fe.pitch !== 0) { cameraImpulse(fe.pitch, 0); addTrauma(fe.trauma); }
    this.wallStats.impacts++; this.wallStats.lastClosing = +closingMps.toFixed(2); this.wallStats.lastGain = snd ? +snd.gain.toFixed(3) : 0; this.wallStats.lastRate = snd ? +snd.rate.toFixed(3) : 0;
  }
  private readonly wallStats = { impacts: 0, lastClosing: 0, lastGain: 0, lastRate: 0 };
  /** The hull's height over the ground under it (the readout's `gap`) — the wash fades with it. */
  private hoverGap = 0;

  /** READ-ONLY: the local ride's downwash for the trample field (physics follow-up A) — absolute XZ, speed, the hull's
   *  height over the ground and its rest ride height; null when not riding. */
  hoverWash(): { x: number; z: number; speed: number; gap: number; hoverM: number } | null {
    if (this.mode !== 'hover' || !this.hoverItem) return null;
    return { x: atlas.localX, z: atlas.localZ, speed: Math.hypot(this.velX, this.velZ), gap: this.hoverGap, hoverM: HOVERCRAFT[this.hoverItem.key]?.hoverM ?? 1 };
  }

  /** Resolve a ship↔structure collision at a candidate position: when the point
   *  is inside a building's solid volume, push the ship back out of the footprint,
   *  apply bounce damage (shield→hull, impact-speed scaled) and a hit explosion,
   *  and return the ejected XZ; else null (free to move there). Shared by the
   *  flight loop and the __shipCombatTest self-check. */
  private collideStructure(nx: number, nz: number, ny: number, speed: number): { x: number; z: number } | null {
    // THE HULL, NOT A POINT (2026-09-23): nose / centre / tail along the heading (the flight loop's
    // forward is −Z of flyQuat), the deepest push-out moves the centre — like `hoverBlocked`. A ship
    // pointed straight up or down has no XZ heading: the probes collapse onto the centre.
    const f = this._hdg.set(0, 0, -1).applyQuaternion(this.flyQuat);
    const hl = Math.hypot(f.x, f.z);
    const fx = hl > 1e-6 ? f.x / hl : 0, fz = hl > 1e-6 ? f.z / hl : 0;
    let bx = 0, bz = 0, bd = -1;
    for (const lever of SHIP_PROBES) {
      const px = nx + fx * lever, pz = nz + fz * lever;
      const hit = structureHitAt(px, pz, ny, SHIP_HALF_W);
      if (!hit) continue;
      const po = structurePushOut(hit, px, pz, SHIP_HALF_W + 2);
      const dx = po.x - px, dz = po.z - pz, d = Math.hypot(dx, dz);
      if (d > bd) { bd = d; bx = dx; bz = dz; }
    }
    if (bd < 0) return null;
    const out = { x: nx + bx, z: nz + bz };
    // The same contact bus as the hovercraft (physics follow-up A): a ship's hull struck on a building thunks (heavier,
    // lower) and shakes the pad; the damage path below already plays the hull hit and the masonry's blast.
    this.wallImpact(speed, SHIP_MASS_T);
    padRumble('crash', Math.min(1, speed / 60));
    this.damage(Math.max(6, speed * 0.4), 'structure collision');
    this.velX *= -0.25; this.velZ *= -0.25;
    this.throttle = Math.min(this.throttle, 0.12);
    this.ensureVfx()?.explosion(this._hit.set(out.x, ny, out.z), 1.7, 0xffb070);
    sfx('sfx_bolt_explosion', { gain: 0.5, rate: 0.8, cooldownMs: 300 }); // the masonry side of the blast, deeper
    this.fxHitCount++;
    return out;
  }

  /** Lazily build the hit-VFX pool and keep its root attached to the LIVE scene
   *  (the scene is swapped across world transitions — re-attach if orphaned). */
  private ensureVfx(): CombatVfx | null {
    const scene = this.app?.world.scene;
    if (!scene) return null;
    if (!this.vfx) this.vfx = new CombatVfx();
    if (this.vfx.root.parent !== scene) scene.add(this.vfx.root);
    return this.vfx;
  }

  /**
   * Advance bolts; a bolt that reaches a STRUCTURE (building volume), the
   * TERRAIN (below the ground surface) or a living CREATURE detonates a hit
   * explosion there (the "you shot something" feedback) and retires. A bolt that
   * simply runs out of range fizzles silently (no explosion — it hit nothing).
   */
  private stepBolts(dt: number): void {
    const conn = atlas.conn;
    const scene = this.app?.world.scene;
    if (!scene || this.bolts.length === 0) return;
    // ONE candidate pass per tick. The old loop iterated EVERY creature row
    // for EVERY bolt with a ground sample each — O(bolts x creatures x 60 Hz)
    // stole the frame budget alongside the per-shot materials.
    const candidates: Array<{ id: bigint; x: number; y: number; z: number }> = [];
    if (conn) {
      try {
        const reach = BOLT_SPEED * BOLT_LIFE_S + 60; // anything a bolt can touch
        for (const c of conn.db.creature.iter()) {
          if (c.state === 'dead') continue;
          const dx = c.x - atlas.localX, dz = c.z - atlas.localZ;
          if (dx * dx + dz * dz > reach * reach) continue;
          candidates.push({ id: c.creatureId, x: c.x, y: groundYIn(conn, atlas.currentWorldId, c.x, c.z) + 1.2, z: c.z });
        }
      } catch { /* row cache mid-churn */ }
    }
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i];
      b.ttl -= dt;
      b.sprite.position.addScaledVector(b.vel, dt);
      const bx = b.sprite.position.x, by = b.sprite.position.y, bz = b.sprite.position.z;
      let dead = false;
      let hitP: THREE.Vector3 | null = null;
      // AIR TARGET: a warship / freighter overhead. War units route the hit
      // to the server (`shootWarAirUnit` in WarAirModule — the server decides
      // the kill via state 'down'); cargo hulls stay client-hp per CargoSky.
      const air = airTargetAt(bx, by, bz, 2);
      if (air) {
        const res = damageAirTarget(air.id, 12);
        if (res === 'hit') sfx('sfx_bolt_hit', { gain: 0.4, cooldownMs: 120 });
        hitP = this._hit.set(bx, by, bz); dead = true;
        // DAMAGE DECAL on the hull (2026-09-08): a blast scorch riding the
        // target's object — ON its mesh: the bolt entered the hit SPHERE (a
        // generous radius), so the mark is the first surface on the way from
        // the bolt to the target's centre; no surface, no mark.
        if (air.obj) {
          // The bolt and the target are ABSOLUTE (a scene child, a row); the hull's meshes are in RENDER space (the rebase
          // holds scene.position at −origin). The ray was cast in absolute coordinates and missed the hull wherever a
          // rebase had run — no scorch on a warship or a freighter, and no damage mark reported. Cast in render space.
          const so = scene.position;
          this._n2.set(air.x + so.x, air.y + so.y, air.z + so.z);
          const sp = surfacePointOn(air.obj, this._rayFrom.set(bx + so.x, by + so.y, bz + so.z), this._n2, air.r * 2.5);
          if (sp) impactDecals(scene).place('blast', sp.point, sp.normal, { parent: air.obj, sizeMul: 0.55 });
          if (sp) { const a = renderToAbsolute(sp.point.clone()); reportDamageHit({ x: a.x, y: a.y, z: a.z, nx: sp.normal.x, ny: sp.normal.y, nz: sp.normal.z, weapon: 'blast', punch: 0.55, mover: { id: `air:${air.id}`, root: air.obj } }); } // shader gaps R2
        }
      }
      // STRUCTURE: the bolt entered a building's solid volume (any altitude).
      if (!dead) {
        const sh = structureHitAt(bx, bz, by, 2);
        if (sh) {
          hitP = this._hit.set(bx, by, bz); dead = true;
          const n = structureFaceNormal(sh, bx, by, bz);
          const fp = structureFacePoint(sh, bx, by, bz); // on the solid's face, not inside its volume
          impactDecals(scene).place('blast', this._n2.set(fp.x, fp.y, fp.z), this._n.set(n.x, n.y, n.z), { sizeMul: 0.9 });
          reportDamageHit({ x: fp.x, y: fp.y, z: fp.z, nx: n.x, ny: n.y, nz: n.z, weapon: 'blast', collider: 'structure', punch: 0.9 }); // shader gaps R2
        }
      }
      // WATER (W1, 2026-10-05): a bolt that met the water this tick ends at the crossing — never on the bed below.
      if (!dead && b.vel.y < 0) {
        const vx = b.vel.x, vy = b.vel.y, vz = b.vel.z;
        const c = firstWaterCrossing(bx - vx * dt, by - vy * dt, bz - vz * dt, vx, vy, vz, Math.hypot(vx, vy, vz) * dt, waterStillAt);
        if (c) { this.boltIntoWater(c.x, waterLiveAt(c.x, c.z) ?? c.y, c.z); dead = true; }
      }
      // TERRAIN: the bolt dropped to/below the ground surface.
      if (!dead && conn) {
        const gy = groundYIn(conn, atlas.currentWorldId, bx, bz);
        if (by <= gy) {
          hitP = this._hit.set(bx, gy + 0.5, bz); dead = true;
          const wid = atlas.currentWorldId;
          const n = groundNormal((x, z) => groundYIn(conn, wid, x, z), bx, bz);
          impactDecals(scene).place('blast', this._n2.set(bx, gy, bz), this._n.set(n.x, n.y, n.z), { sizeMul: 1.1 });
          reportGroundHit(bx, gy, bz, n.x, n.y, n.z, 'blast', 1.1); // shader gaps R2: the cannon gouges the ground
        }
      }
      // CREATURE: proximity strike (server-validated) — also an impact burst.
      if (!dead && conn) {
        for (const c of candidates) {
          const d = Math.hypot(c.x - bx, c.y - by, c.z - bz);
          if (d < 7) {
            const shipD = Math.hypot(c.x - atlas.localX, c.y - atlas.localY, c.z - atlas.localZ);
            if (shipD <= SHIP_CANNON_RANGE_M - 20) {
              conn.reducers.shipStrikeCreature({ creatureId: c.id })
                .then(() => sfx('sfx_creature_hit', { gain: 0.35, cooldownMs: 250 })) // strafe hit confirm at altitude
                .catch(() => { /* cadence/range — the next bolt lands */ });
            }
            hitP = this._hit.set(c.x, c.y, c.z); dead = true;
            break;
          }
        }
      }
      if (!dead && b.ttl <= 0) dead = true; // fizzled at range — hit nothing, no VFX
      if (dead) {
        if (hitP) {
          this.ensureVfx()?.explosion(hitP, 1.0);
          const dBoom = Math.hypot(hitP.x - atlas.localX, hitP.y - atlas.localY, hitP.z - atlas.localZ);
          // fire realism R5: the report arrives at the speed of sound (a head-locked cue, so held back by sfxLater).
          const boomGain = Math.max(0.1, 0.5 - dBoom * 0.001);
          sfxLater(Math.round(explosionSoundDelayS(hitP) * 1000), () => sfx('sfx_bolt_explosion', { gain: boomGain, cooldownMs: 110 }));
          this.fxHitCount++;
        }
        // Pooled — NEVER dispose the shared material, never churn the scene.
        b.sprite.visible = false;
        this.boltPool.push(b.sprite);
        this.bolts.splice(i, 1);
      }
    }
  }

  private clearBolts(): void {
    for (const b of this.bolts) { b.sprite.visible = false; this.boltPool.push(b.sprite); }
    this.bolts = [];
  }

  /** The ACTIVE ship's real stats (chassis × equipped component quality). */
  private refreshShipStats(): void {
    const conn = atlas.conn;
    let chassis = this.flyChassis || DEFAULT_CHASSIS;
    const comps: Array<{ slot: string; itemKey: string; quality: number }> = [];
    try {
      if (conn?.identity) {
        let row: { shipId: bigint; chassisKey: string; active: boolean } | null = null;
        for (const sh of conn.db.playerShip.iter()) {
          if (!sh.owner.isEqual(conn.identity)) continue;
          if (sh.active) { row = sh; break; }
          row ??= sh;
        }
        if (row) {
          if (CHASSIS[row.chassisKey]) chassis = row.chassisKey;
          for (const c of conn.db.shipComponent.iter()) {
            if (c.shipId === row.shipId) comps.push({ slot: c.slot, itemKey: c.itemKey, quality: c.quality });
          }
        }
      }
    } catch { /* row cache mid-churn — keep the last stats */ }
    this.stats = shipStats(chassis, comps);
    this.hull = Math.min(this.hull, this.stats.hp);
    this.shield = Math.min(this.shield, this.stats.shield);
    this.cap = Math.min(this.cap, this.stats.capacitor);
  }

  private async ascend(): Promise<void> {
    const conn = atlas.conn;
    if (!conn) { this.ascending = false; return; }
    cloudTunnel.hold(0.7); // the swap happens INSIDE the deck — full white first
    this.status('Punching through the cloud deck — the sector takes you…');
    try {
      await conn.reducers.ascendToSpace({});
    } catch (e) {
      cloudTunnel.cancel();
      this.status(`Ascent refused: ${(e as Error).message ?? e}`);
      this.ascending = false;
      return;
    }
    // The server moved our playerState into the space world — adopt it
    // (the AtlasTravelModule.launch contract).
    this.exitFlight();
    let spaceId: bigint | null = null;
    for (let i = 0; i < 40 && spaceId === null; i++) {
      for (const w of conn.db.world.iter()) if (w.kind === 'space') { spaceId = w.worldId; break; }
      if (spaceId === null) await new Promise((r) => setTimeout(r, 250));
    }
    if (spaceId === null) { cloudTunnel.cancel(); this.status('The sector did not answer — try again'); return; }
    const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    bus.emit('atlas:world-adopted', { worldId: spaceId, x: me?.posX ?? 0, z: me?.posZ ?? 0 });
    // Break out of the deck only once the SECTOR is truly entered
    // (subscriptions resolved + SpaceModule activated) — a fixed timer
    // revealed mid-resubscribe with the commons still rendered below
    // (the huge-ship-over-the-city family, 2026-08-23). 25 s failsafe.
    const off = bus.on('atlas:world-entered', () => { off(); cloudTunnel.reveal(1.6, 3.4); });
    window.setTimeout(() => { off(); if (cloudTunnel.scripted) cloudTunnel.reveal(0.4, 3.0); }, 25_000);
  }

  /** Mid-flight resume (login while airborne) + world-hop cleanup. */
  private onWorldEntered(): void {
    // The space handoff owns its own teardown mid-ascent — leave it alone.
    if (this.ascending) { this.resumeChecked = true; return; }
    // A world hop ends any active client-side ride (you don't carry a vehicle
    // through a portal); the server row is the truth.
    if (this.mode === 'hover') { this.mode = 'none'; atlas.hoverRide = false; this.hoverItem = null; this.clearRideMesh(); sfxLoop('sfx_hover_engine', 'vehicle').stop(0.3); sfxLoop('sfx_hover_scrape', 'vehicle').stop(0.2); sfxLoop('sfx_water_spray', 'vehicle').stop(0.3); this.wallScrape = newScrape(); }
    else if (this.mode === 'fly') { this.exitFlight(); }
    // Then poll for a RESUME (a fly: row in the world we just entered) — works on
    // any ground world now, not just the Commons.
    this.resumeChecked = false;
  }

  private tryResume(): void {
    const conn = atlas.conn;
    if (!conn?.identity) return;
    const me = conn.db.playerState.identity.find(conn.identity);
    if (!me) return;
    this.resumeChecked = true;
    if (me.animState.startsWith('fly:') && this.mode === 'none' && !atlas.spaceFlight) {
      atlas.localY = Math.max(atlas.localY, me.posY);
      this.enterFlight();
    }
  }

  // ─── systems ──────────────────────────────────────────────────────────────

  private fixed(): void {
    this.wallSimMs += FIXED_DT * 1000; // the wall-contact cooldown's clock (sim time — a slow renderer never mutes a hit)
    if (!this.resumeChecked && this.onGroundWorld() && !atlas.loadingGate) this.tryResume();
    if (this.mode === 'hover') this.hoverFixed();
    else if (this.mode === 'fly') this.flyFixed();
  }

  private frame(dt: number): void {
    cloudTunnel.update(dt, this.app); // the deck ticks in EVERY world (it spans the swap)
    if (this.vfx) this.vfx.update(dt); // advance hit explosions (bounded, self-reaping)
    // Wind-rush layer: the cloud deck ROARS as it thickens (both directions).
    sfxLoop('sfx_cloud_rush', 'vehicle').set(cloudTunnel.intensity * 0.55, 1, 0.3);
    this.updateRideMesh(dt);
    this.berthAcc += dt;
    if (this.berthAcc >= 2) { this.berthAcc = 0; this.syncBerths(); this.syncRemotes(); }
    this.updateRemoteFx(dt);
    this.updateHud();
  }

  // ─── meshes ───────────────────────────────────────────────────────────────

  /** A stylized fallback skimmer (tier-colored) until the GLB pack lands. */
  private makeHoverMesh(key: string): THREE.Object3D {
    const spec: HovercraftSpec | undefined = HOVERCRAFT[key];
    const tier = spec?.tier ?? 1;
    const g = new THREE.Group();
    const hull = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.35, 2.6),
      new THREE.MeshStandardMaterial({ color: TIER_COLOR[tier], roughness: 0.55, metalness: 0.45 }),
    );
    hull.position.y = 0.45;
    const skirt = new THREE.Mesh(
      new THREE.CylinderGeometry(1.05, 1.25, 0.28, 12),
      new THREE.MeshStandardMaterial({ color: 0x2a2f38, roughness: 0.8 }),
    );
    skirt.position.y = 0.18;
    const coil = new THREE.Mesh(
      new THREE.TorusGeometry(0.55, 0.07, 8, 20),
      new THREE.MeshStandardMaterial({ color: 0x223, emissive: 0x4f8cf0, emissiveIntensity: 0.9, roughness: 0.4 }),
    );
    coil.rotation.x = Math.PI / 2;
    coil.position.y = 0.08;
    g.add(hull, skirt, coil);
    return g;
  }

  /** A stylized fallback hull until/unless the fleet GLB resolves —
   *  base at y=0 so a berthed placeholder never sinks into the ground. */
  private makeShipFallback(): THREE.Object3D {
    const g = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.ConeGeometry(1.1, 4.4, 6),
      new THREE.MeshStandardMaterial({ color: 0x8a93a6, roughness: 0.4, metalness: 0.7 }),
    );
    body.rotation.x = -Math.PI / 2; // nose forward (−Z after group yaw)
    body.position.y = 1.15;
    const fin = new THREE.Mesh(
      new THREE.BoxGeometry(3.4, 0.12, 1.2),
      new THREE.MeshStandardMaterial({ color: 0x5a6374, roughness: 0.5, metalness: 0.6 }),
    );
    fin.position.y = 1.15;
    g.add(body, fin);
    return g;
  }

  /** Resolve (and cache) a hull master by model key ('prop:<key>'); null while loading/absent. */
  private hullMaster(key: string): THREE.Object3D | null {
    const have = this.shipMasters.get(key);
    if (have) return have;
    const conn = atlas.conn;
    if (!conn || !this.assets || this.shipLoading.has(key)) return null;
    const id = prefabAssetId(conn, `prop:${key}`);
    if (id === null) return null;
    this.shipLoading.add(key);
    this.assets.load(id)
      .then((cached) => {
        // AssetModule.load() returns a CACHED, SHARED scene root — SpaceModule
        // loads the same id for the sector ship. NEVER mutate it: scaling the
        // cache made SpaceModule's own box-fit measure an already-fitted hull,
        // compute ×1, and reset the ship to raw GLB size — the tiny-ship-in-
        // space field bug (2026-08-20). Normalize a PRIVATE clone instead
        // (clone shares geometry/materials, which markShared flags as such).
        const obj = cached.clone();
        // Raw GLB scale is arbitrary — box-fit every hull at load: hovercraft
        // to the catalog footprint, fleet ships to their chassis hullSize
        // (the SpaceModule wrapHull contract — the berthed hull must read
        // the same size it does in the sector).
        const target = HOVERCRAFT[key] ? HOVER_MODEL_LEN_M : (CHASSIS[key]?.hullSize ?? 12);
        const box = new THREE.Box3().setFromObject(obj);
        const size = new THREE.Vector3();
        box.getSize(size);
        const longest = Math.max(size.x, size.y, size.z);
        if (longest > 1e-3) obj.scale.multiplyScalar(target / longest);
        markShared(obj); // clones tear down freely; the master's buffers survive
        this.shipMasters.set(key, obj);
      })
      .catch(() => { /* fallback hull covers it */ })
      .finally(() => { this.shipLoading.delete(key); });
    return null;
  }

  /** The hull an animState tag wants ('hover:<key>' | 'fly:<chassis>'). */
  /** A hovercraft body for someone else to drive (the racing module's rivals): the cooked hull when its master is
   *  loaded, else the procedural skimmer. Its origin is the hull; the caller seats and turns it. */
  hovercraftBody(key: string): THREE.Object3D { return this.buildVehicle(`hover:${key}`, false); }
  /** Is the cooked hull loaded yet (asking starts its load)? A body built before it is the procedural skimmer. */
  hovercraftReady(key: string): boolean { return this.hullMaster(key) !== null; }

  private buildVehicle(tag: string, seated = true): THREE.Object3D {
    if (tag.startsWith('hover:')) {
      const key = tag.slice(6);
      const master = this.hullMaster(key);
      if (master) {
        const obj = master.clone();
        obj.rotation.y = HOVER_MODEL_YAW;
        const wrap = new THREE.Group();
        wrap.add(obj);
        // THE SEAT (2026-10-08): a craft with a measured seat is moved inside its group so the group's ORIGIN is the seat
        // (`userData.seatOrigin` tells the two placers below). No row = the hull's bottom centre, as it always was.
        // `seated` false = the bare hull about its own origin (a race rival: the caller seats it on the ground).
        const seat = seated ? hoverSeat(key) : null;
        if (seat) { const o = seatHullOffset(seat); obj.position.set(o.x, o.y, o.z); wrap.userData.seatOrigin = true; }
        return wrap;
      }
      return this.makeHoverMesh(key);
    }
    const chassis = tag.slice(4);
    const master = this.hullMaster(chassis);
    if (master) {
      const obj = master.clone();
      obj.rotation.y = SHIP_MODEL_YAW;
      const wrap = new THREE.Group();
      wrap.add(obj);
      return wrap;
    }
    return this.makeShipFallback();
  }

  private rideTag = '';
  private rideIsFallback = false;
  private savedCamPos: THREE.Vector3 | null = null;
  /** First-person cockpit for atmospheric flight — the same canopy the space
   *  sector uses; the camera moves to the ship's nose, flight unchanged. */
  private cockpitView = false;
  private readonly cockpitOverlay = new CockpitOverlay();
  /** Scroll-wheel camera distance (rig-local): in past ZOOM_COCKPIT → cockpit. */
  private flyCamBack = ZOOM_CHASE;
  /** The fly camera's rig-local base position (applyFlyZoom) and the chase's lag + bank (physics follow-up A). */
  private readonly flyCamBase = { x: 0, y: 0, z: 0 };
  private readonly flyChase = newChase();
  private readonly chaseDiag = { lag: 0, roll: 0, k: 1, active: false };

  /** Y (in fly mode): snap between chase + cockpit (a shortcut for the scroll). */
  private toggleCockpit(): void {
    if (this.mode !== 'fly') return;
    this.flyCamBack = this.cockpitView ? chaseFor(CHASSIS[this.flyChassis]) : ZOOM_MIN + 0.5;
    this.applyFlyZoom();
    this.status(this.cockpitView ? `Cockpit view — ${(CHASSIS[this.flyChassis] ?? CHASSIS[DEFAULT_CHASSIS]).name} (scroll to zoom)` : 'Chase view (scroll to zoom)');
  }

  /** Position the fly camera from the scroll distance: first-person canopy when
   *  zoomed in, else a chase that widens with distance. */
  private applyFlyZoom(): void {
    const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
    if (!cam || this.mode !== 'fly') return;
    const spec = CHASSIS[this.flyChassis] ?? CHASSIS[DEFAULT_CHASSIS];
    const zc = zoomCamera(this.flyCamBack, cockpitEye(spec));
    cam.position.set(zc.x, zc.y, zc.z);
    this.flyCamBase.x = zc.x; this.flyCamBase.y = zc.y; this.flyCamBase.z = zc.z; // the chase orbits this by its lag, per frame
    this.cockpitView = zc.cockpit;
    if (zc.cockpit) {
      this.cockpitOverlay.setChassis(spec);
      this.cockpitOverlay.show();
      if (this.rideMesh) this.rideMesh.visible = false;
    } else {
      this.cockpitOverlay.hide();
      if (this.rideMesh) this.rideMesh.visible = true;
    }
  }

  private applyCockpitView(): void { this.applyFlyZoom(); }

  /** Third-person chase while mounted (the ship-flight feel): slide the rig's
   *  camera back and up; restored exactly on dismount/land/ascend. */
  private applyVehicleCamera(tag: string): void {
    if (!tag.startsWith('fly:')) return; // the hover chase is posed per frame by the avatar module (scroll zoom)
    const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
    if (!cam) return;
    if (!this.savedCamPos) this.savedCamPos = cam.position.clone();
    const o = tag.startsWith('fly:') ? FLY_CAM_OFFSET : HOVER_CAM_OFFSET;
    cam.position.set(0, this.savedCamPos.y + o.y, o.z);
    this.flyCamBase.x = 0; this.flyCamBase.y = this.savedCamPos.y + o.y; this.flyCamBase.z = o.z; // the per-frame chase orbits this
  }

  private restoreCamera(): void {
    const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
    if (cam && this.savedCamPos) cam.position.copy(this.savedCamPos);
    this.savedCamPos = null;
  }

  private ensureRideMesh(tag: string): void {
    if (this.rideTag === tag && this.rideMesh) return;
    this.clearRideMesh();
    this.rideTag = tag;
    const key = tag.startsWith('fly:') ? tag.slice(4) : tag.slice(6);
    this.rideIsFallback = !this.shipMasters.has(key);
    this.rideMesh = this.buildVehicle(tag);
    reattachDamageMover(`ride:${tag}`, this.rideMesh); // shader gaps R2: a rebuilt ride keeps the marks this hull took
    this.app?.world.scene.add(this.rideMesh);
    this.buildRideVfx(tag, this.rideMesh);
    // Engines + dust, fitted to THIS hull (the fallback box or the resolved GLB).
    if (tag.startsWith('hover:') && this.app) this.rideFx = new VehicleFx(key, this.rideMesh, this.app.world.scene);
    this.applyVehicleCamera(tag);
  }

  /** Heat haze needs a scene copy (`viewportSharedTexture`); on WebGPU that
   *  mid-pass copy corrupted the encoder in r182 (the water doctrine), so it
   *  is WebGL2-only unless 'atlas:haze-webgpu' = 'on' (the probe's lever). */
  private hazeAllowed(): boolean {
    if (!activePreset().vfxShaders) return false;
    const gpu = this.app?.renderer.isWebGPU ?? false;
    if (!gpu) return true;
    try { return localStorage.getItem('atlas:haze-webgpu') === 'on'; } catch { return false; }
  }

  private buildRideVfx(tag: string, hull: THREE.Object3D): void {
    if (!activePreset().vfxShaders) return;
    hull.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(hull);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
    hull.worldToLocal(c);
    const inv = 1 / Math.max(1e-6, hull.getWorldScale(new THREE.Vector3()).x);
    // Shield shell: an ellipsoid a little larger than the hull, invisible until a hit.
    try {
      const { material, uniforms } = createShieldMaterial({ color: [0.35, 0.8, 1.0], rimPower: 2.4, intensity: 0, scanFrequency: 5, scanSpeed: 3, scanStrength: 0.35, intersectColor: [0.7, 0.95, 1.0], intersectDistance: 0.35, cameraFar: 120_000 });
      const shell = new THREE.Mesh(new THREE.SphereGeometry(1, 28, 18), material);
      shell.name = 'ride-shield';
      shell.position.copy(c);
      shell.scale.set(size.x * 0.58 * inv, size.y * 0.7 * inv, size.z * 0.58 * inv);
      shell.visible = false;
      shell.renderOrder = 7;
      hull.add(shell);
      this.rideShield = { mesh: shell, intensity: uniforms.intensity };
      stage3Stats.shieldShells++;
    } catch { /* best-effort */ }
    // Heat haze behind a hovercraft's engines (camera-facing quad, strength by speed).
    if (tag.startsWith('hover:') && this.hazeAllowed()) {
      try {
        // surfaces (2026-09-27): THE HAZE COPY — the format-safe scene copy (lib/vfx/fire_volume hazeSceneColor; the WebGL2
        // glCopyTexSubImage2D float → unorm error), `atlas:haze-copy` = 'shared' restores three's node for the repro.
        const { material, uniforms } = createHeatHazeMaterial({ strength: 0, noiseScale: 5, speed: 2.4, edgeSoftness: 0.5, sceneColor: hazeSceneColor() as never });
        const quad = new THREE.Mesh(new THREE.PlaneGeometry(size.x * 0.9 * inv, size.y * 1.6 * inv), material);
        quad.name = 'ride-haze';
        quad.position.set(c.x, c.y + size.y * 0.15 * inv, c.z + size.z * 0.55 * inv); // aft (local +Z is astern)
        quad.visible = false;
        quad.renderOrder = 9;
        hull.add(quad);
        this.rideHaze = { mesh: quad, strength: uniforms.strength };
        stage3Stats.hazeQuads++;
      } catch { /* best-effort */ }
    }
  }

  /** Per frame: shield flash decay + haze strength/billboarding (stage 3). */
  private stepRideVfx(speed: number, top: number): void {
    if (this.rideShield) {
      const since = this.shieldHitAt < 0 ? -1 : (performance.now() - this.shieldHitAt) / 1000;
      const v = shieldIntensity(since);
      this.rideShield.intensity.value = v;
      this.rideShield.mesh.visible = v > 0;
    }
    if (this.rideHaze) {
      const v = hazeStrength(speed, top);
      this.rideHaze.strength.value = v;
      this.rideHaze.mesh.visible = v > 0;
      stage3Stats.haze = v;
      if (v > 0) {
        const cam = this.app?.renderer.cameras.main;
        if (cam) { cam.getWorldPosition(this._hazeCam); this.rideHaze.mesh.lookAt(this._hazeCam); }
      }
    }
  }
  private readonly _hazeCam = new THREE.Vector3();

  private clearRideMesh(): void {
    if (this.rideFx) { this.rideFx.dispose((r) => disposeLater(r)); this.rideFx = null; } // deferred (warp-teardown law)
    if (this.rideMesh) {
      this.app?.world.scene.remove(this.rideMesh);
      deferDisposeTree(this.rideMesh); // the haze quad + shield shell go with it (children)
      this.rideMesh = null;
      this.rideHaze = null; this.rideShield = null;
    }
    this.rideTag = '';
    this.restoreCamera();
  }

  private updateRideMesh(dt: number): void {
    const m = this.rideMesh;
    if (!m) return;
    // A GLB master may have resolved since the fallback hull was built — upgrade in place.
    const rideKey = this.rideTag.startsWith('fly:') ? this.rideTag.slice(4) : this.rideTag.slice(6);
    if (this.rideIsFallback && rideKey && this.shipMasters.has(rideKey)) {
      const tag = this.rideTag;
      this.rideTag = '';
      this.ensureRideMesh(tag);
      return;
    }
    if (this.mode === 'hover') {
      // A seated craft's group origin is its SEAT: it rides at the rider's seat (the rider's root + RIDER_SEAT_M), whatever
      // the ride height; every other craft keeps its underside 0.15 m over the ground under the rider.
      m.position.set(atlas.localX, m.userData.seatOrigin === true ? atlas.localY + RIDER_SEAT_M : atlas.localY - (this.hoverItem ? HOVERCRAFT[this.hoverItem.key].hoverM : 1) + 0.15, atlas.localZ);
      // Yaw to heading, then BANK into the turn (roll) and PITCH on ramps/climb —
      // both from the physics readout — smoothed for a settled, skimmer feel.
      m.rotation.order = 'YXZ';
      m.rotation.y = atlas.localYaw;
      const kk = Math.min(1, dt * 7);
      m.rotation.z += (this.hoverBank - m.rotation.z) * kk;
      m.rotation.x += (this.hoverPitch - m.rotation.x) * kk;
      this.stepRideVfx(Math.hypot(this.velX, this.velZ), this.hoverItem ? hovercraftSpeed(HOVERCRAFT[this.hoverItem.key], 500) : 40);
      if (this.rideFx) {
        this.rideFx.update({
          dt,
          speed: Math.hypot(this.velX, this.velZ),
          throttle: keyHeldIn(this.keys, 'throttleUp') ? 1 : 0,
          boost: this.wasBoosting,
          turbo: this.hoverTurboPrev > 0,
          grounded: !this.hoverAirborne,
          onWater: this.hoverOnWater,
          drifting: this.hoverDrifting,
        });
      }
    } else if (this.mode === 'fly') {
      m.position.set(atlas.localX, atlas.localY - 0.8, atlas.localZ);
      m.quaternion.copy(this.flyQuat);
      this.stepRideVfx(0, 0); // the shield flash decays on a ship too (no haze in flight)
      // Keep the chase rig on the nose: yaw to the body, pitch to the camera.
      const e = new THREE.Euler().setFromQuaternion(this.flyQuat, 'YXZ');
      atlas.localYaw = e.y;
      const cam = atlas.localMesh?.children[0] as THREE.Object3D | undefined;
      // THE CHASE TRAILS AND BANKS (physics follow-up A, lib/chase_camera.ts): the chase camera's yaw follows the hull's
      // through a critically damped spring and rolls with the turn — the CAMERA only (flyQuat, the guns and the row keep
      // the true heading); scaled by the camera-motion row; never in the cockpit (the eye is IN the hull) or a headset.
      // The feel layer's kick / shake reach the flight camera here (the avatar's feel block stands down in skyFlight).
      const motionK = atlas.xrPresenting ? 0 : cameraMotionScale(false);
      const feelOff = atlas.xrPresenting ? null : scaleOffsets(stepFeel(dt, performance.now() / 1000, 0, 0), motionK);
      let lag = 0, roll = 0;
      const chaseOn = !this.cockpitView && motionK > 0;
      if (chaseOn) { const c = stepChase(this.flyChase, e.y, dt); lag = c.lag * motionK; roll = c.roll * motionK; }
      else resetChase(this.flyChase, e.y);
      this.chaseDiag.lag = lag; this.chaseDiag.roll = roll; this.chaseDiag.k = motionK; this.chaseDiag.active = chaseOn;
      if (cam) {
        cam.rotation.order = 'YXZ';
        cam.rotation.x = e.x * 0.55 + (feelOff?.pitch ?? 0);
        cam.rotation.y = lag + (feelOff?.yaw ?? 0);
        cam.rotation.z = roll + (feelOff?.roll ?? 0);
        const p = orbitChase(this.flyCamBase, lag);
        cam.position.set(p.x, p.y, p.z);
      }
      if (this.cockpitView) this.cockpitOverlay.draw(-e.z * 0.5); // canopy banks with the roll
    }
  }

  // ─── berthed ships (everyone's) ───────────────────────────────────────────

  /** The parked ships near the viewer (the fauna's perches, 2026-09-11): hull centre, deck top, half-size. */
  parkedShips(): Array<{ x: number; y: number; z: number; top: number; half: number }> {
    const out: Array<{ x: number; y: number; z: number; top: number; half: number }> = [];
    for (const [, e] of this.berthMeshes) {
      const size = CHASSIS[e.chassis]?.hullSize ?? 12;
      out.push({ x: e.obj.position.x, y: e.obj.position.y, z: e.obj.position.z, top: e.obj.position.y + size * 0.22, half: size * 0.35 });
    }
    return out;
  }

  private syncBerths(): void {
    const conn = atlas.conn;
    const scene = this.app?.world.scene;
    if (!conn || !scene) return;
    const seen = new Set<string>();
    if (this.inCommons()) {
      try {
        for (const b of conn.db.shipBerth.iter()) {
          if (atlas.commonsWorldId !== null && b.worldId !== atlas.commonsWorldId) continue;
          // My own berth row can outlive my takeoff by a replication beat —
          // while I fly, a stale row must never resurrect the ghost hull.
          if (this.mode === 'fly' && conn.identity && b.identity.isEqual(conn.identity)) continue;
          const hex = b.identity.toHexString();
          seen.add(hex);
          let entry = this.berthMeshes.get(hex);
          // Rebuild on a chassis change OR once the GLB master resolves —
          // the placeholder cone must never outlive the real hull.
          if (entry && (entry.chassis !== b.chassisKey || (entry.fallback && this.shipMasters.has(b.chassisKey)))) {
            scene.remove(entry.obj);
            deferDisposeTree(entry.obj);
            entry = undefined;
          }
          if (!entry) {
            entry = { obj: this.buildVehicle(`fly:${b.chassisKey}`), chassis: b.chassisKey, fallback: !this.shipMasters.has(b.chassisKey) };
            scene.add(entry.obj);
            this.berthMeshes.set(hex, entry);
          }
          // Slope-aware seating: sample the footprint, sit on the HIGHEST
          // corner — a long hull on a hillside must never clip the ground.
          const half = (CHASSIS[b.chassisKey]?.hullSize ?? 12) * 0.35;
          let ground = groundYIn(conn, atlas.commonsWorldId, b.posX, b.posZ);
          for (const [dx, dz] of [[half, 0], [-half, 0], [0, half], [0, -half]] as const) {
            ground = Math.max(ground, groundYIn(conn, atlas.commonsWorldId, b.posX + dx, b.posZ + dz));
          }
          entry.obj.position.set(b.posX, ground + 0.25, b.posZ);
          entry.obj.rotation.y = b.yaw;
        }
      } catch { /* row cache mid-churn */ }
    }
    for (const [hex, entry] of this.berthMeshes) {
      if (!seen.has(hex)) {
        scene.remove(entry.obj);
        deferDisposeTree(entry.obj);
        this.berthMeshes.delete(hex);
      }
    }
  }

  // ─── remote riders/pilots ─────────────────────────────────────────────────

  private syncRemotes(): void {
    const scene = this.app?.world.scene;
    if (!scene) return;
    const seen = new Set<string>();
    for (const r of atlas.remotes.values()) {
      const st = r.animState;
      const tag = st.startsWith('ride:') ? `hover:${st.slice(5)}` : st.startsWith('fly:') ? st : '';
      if (!tag || !r.mesh) continue;
      seen.add(r.identityHex);
      const tagKey = tag.startsWith('fly:') ? tag.slice(4) : tag.slice(6);
      let rig = this.remoteRigs.get(r.identityHex);
      if (rig && (rig.tag !== tag || (rig.fallback && this.shipMasters.has(tagKey)))) {
        rig.obj.removeFromParent();
        deferDisposeTree(rig.obj);
        rig = undefined;
      }
      if (!rig) {
        rig = { obj: this.buildVehicle(tag), tag, fallback: !this.shipMasters.has(tagKey) };
        rig.obj.position.y = tag.startsWith('hover:') ? (rig.obj.userData.seatOrigin === true ? RIDER_SEAT_M : -0.75) : -0.8; // deck under the rider's feet (a seated craft: its seat under the rider)
        r.mesh.add(rig.obj);
        this.remoteRigs.set(r.identityHex, rig);
        // Remote hovercraft carry the same engines + dust, fitted to their hull.
        const oldFx = this.remoteFx.get(r.identityHex);
        if (oldFx) { oldFx.fx.dispose((x) => disposeLater(x)); this.remoteFx.delete(r.identityHex); }
        if (tag.startsWith('hover:') && scene) {
          this.remoteFx.set(r.identityHex, { fx: new VehicleFx(tagKey, rig.obj, scene), lastX: r.targetX, lastZ: r.targetZ, lastT: performance.now(), speed: 0 });
        }
      } else if (rig.obj.parent !== r.mesh) {
        r.mesh.add(rig.obj); // proxy was rebuilt (appearance change)
      }
      // (The per-frame engines / dust / spray moved to `updateRemoteFx`, W3 2026-10-05: this sync runs every 2 s.)
    }
    for (const [hex, rig] of this.remoteRigs) {
      if (!seen.has(hex)) {
        rig.obj.removeFromParent();
        deferDisposeTree(rig.obj);
        this.remoteRigs.delete(hex);
        const rf = this.remoteFx.get(hex);
        if (rf) { rf.fx.dispose((x) => disposeLater(x)); this.remoteFx.delete(hex); }
      }
    }
  }

  /**
   * W3 (2026-10-05): other riders' engines, dust and SPRAY, every frame. The FX update lived inside `syncRemotes`,
   * which runs every 2 s, and passed a fixed dt of 1/60 with `onWater: false` hard-coded — a remote skimmer's plume and
   * dust froze between 2 s steps, and over a lake it threw dust, never spray, and left no wake. Now: the speed from the
   * authoritative target's motion (an EMA), on water judged from the live surface under the hull (within
   * HOVER_ON_WATER_M), the real dt; and every remote hull REPORTS itself (silent — `atlas.remotes` is voiced already) so
   * the water's one craft pass wakes the surface under a skimmer and raises downwash under a low ship. Absolute coords.
   */
  private updateRemoteFx(dt: number): void {
    const now = performance.now();
    let onWaterN = 0;
    for (const r of atlas.remotes.values()) {
      const st = r.animState;
      const riding = st.startsWith('ride:'), flying = st.startsWith('fly:');
      if ((!riding && !flying) || !r.mesh) continue;
      const p = r.mesh.position; // a scene child: ABSOLUTE (the FP64 rebase law)
      let cid = this.remoteCraftIds.get(r.identityHex);
      if (!cid) { cid = `remote:${r.identityHex}`; this.remoteCraftIds.set(r.identityHex, cid); }
      if (flying) {
        const hull = CHASSIS[st.slice(4)]?.hullSize ?? 12;
        this.remoteReport.hullM = hull; this.remoteReport.ownSpray = false;
        reportCraft(cid, 'ship', p.x, p.y - 0.8, p.z, now, this.remoteReport);
        continue;
      }
      const rf = this.remoteFx.get(r.identityHex);
      if (!rf) continue;
      const dtS = Math.max(1e-3, (now - rf.lastT) / 1000);
      if (r.targetX !== rf.lastX || r.targetZ !== rf.lastZ) {
        const v = Math.hypot(r.targetX - rf.lastX, r.targetZ - rf.lastZ) / dtS;
        rf.speed += (Math.min(v, 320) - rf.speed) * 0.5;
        rf.lastX = r.targetX; rf.lastZ = r.targetZ; rf.lastT = now;
      } else if (dtS > 0.6) {
        rf.speed *= Math.exp(-2.5 * Math.min(0.1, dt)); // no fresh row for a while — coasting down (frame-rate free)
      }
      const live = waterLiveAt(p.x, p.z);
      const onWater = live !== null && p.y - live <= HOVER_ON_WATER_M;
      if (onWater) onWaterN++;
      rf.fx.update({ dt: Math.min(0.1, Math.max(1e-3, dt)), speed: rf.speed, throttle: rf.speed > 1 ? Math.min(1, rf.speed / 20) : 0, boost: false, turbo: false, grounded: true, onWater, drifting: false });
      // The hull draws its own spray (VehicleFx); the water pass adds the wake + the hiss.
      this.remoteReport.hullM = 4.2; this.remoteReport.ownSpray = true;
      reportCraft(cid, 'hover', p.x, p.y, p.z, now, this.remoteReport);
    }
    this.remoteWaterCount = onWaterN;
    if (this.remoteCraftIds.size > 64) this.remoteCraftIds.clear(); // a cache of strings, rebuilt on demand
  }
  private remoteWaterCount = 0;
  private readonly remoteReport = { hullM: 0, silent: true, ownSpray: false };

  // ─── HUD ──────────────────────────────────────────────────────────────────

  private buildHud(): void {
    const hud = document.createElement('div');
    hud.id = 'vehicle-hud';
    hud.style.cssText = 'position:fixed;top:64px;left:50%;transform:translateX(-50%);z-index:40;'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);background:color-mix(in srgb, var(--ax-bg) 72%, transparent);border:1px solid var(--ax-line);'
      + 'border-radius:var(--ax-radius);padding:6px 14px;display:none;pointer-events:none;letter-spacing:.02em';
    document.body.appendChild(hud);
    this.hud = hud;
    const hint = document.createElement('div');
    hint.id = 'vehicle-hint';
    hint.style.cssText = 'position:fixed;bottom:96px;left:50%;transform:translateX(-50%);z-index:40;'
      + 'font:600 13px var(--ax-font);color:var(--ax-hi);background:color-mix(in srgb, color-mix(in srgb, var(--ax-accent) 22%, var(--ax-bg2)) 72%, transparent);border:1px solid var(--ax-line);'
      + 'border-radius:var(--ax-radius);padding:6px 14px;display:none;pointer-events:none';
    document.body.appendChild(hint);
    this.hint = hint;
  }

  // Last strings written to the two HUD elements — innerHTML is a full parse
  // + subtree rebuild, and updateHud runs every Animation frame; an unchanged
  // hint/readout is now a string compare instead of a DOM mutation.
  private hudHtml = '';
  private hintHtml = '';
  private setHudHtml(el: HTMLElement, html: string, which: 'hud' | 'hint'): void {
    if (which === 'hud') { if (html === this.hudHtml) return; this.hudHtml = html; }
    else { if (html === this.hintHtml) return; this.hintHtml = html; }
    el.innerHTML = html;
  }

  private updateHud(): void {
    const hud = this.hud, hint = this.hint;
    if (!hud || !hint) return;
    const conn = atlas.conn;
    if (this.mode === 'fly' && conn) {
      const ground = groundYIn(conn, atlas.currentWorldId, atlas.localX, atlas.localZ);
      const agl = Math.max(0, atlas.localY - ground);
      const spd = Math.round(Math.hypot(this.velX, this.velY, this.velZ));
      // Only the Commons sky opens to space; a personal / spawned world just has a ceiling.
      const asc = this.inCommons() && atlas.localY >= ASCEND_WARN_Y
        ? ` · <span style="color:var(--ax-hi)">the sky thins — keep climbing (${Math.round(ASCEND_Y - atlas.localY)} m)</span>` : '';
      const cruiseTag = this.cruise > 1.05 ? ` · <span style="color:var(--ax-hi)">cruise ×${this.cruise.toFixed(1)}</span>` : '';
      this.setHudHtml(hud,
        `✈ ${Math.round(agl)} m · ${spd} m/s · thr ${Math.round(this.throttle * 100)}%${cruiseTag}`
        + ` · <span style="color:var(--ax-good)">hull ${Math.max(0, Math.round(this.hull))}/${this.stats.hp}</span>`
        + ` · <span style="color:var(--ax-hi)">shd ${Math.round(this.shield)}/${this.stats.shield}</span>`
        + ` · <span style="color:var(--ax-hi)">cap ${Math.round(this.cap)}</span>`
        + ` · <span style="color:var(--ax-warn)">boost ${Math.round(this.boosterE)}</span>${asc}`, 'hud');
      hud.style.display = 'block';
      if (agl <= LAND_MAX_AGL) {
        const blocker = this.landBlocker(atlas.localX, atlas.localZ);
        this.setHudHtml(hint, blocker
          ? (blocker === 'water' ? '✗ open water — no landing' : `✗ ${blocker} airspace — no landing`)
          : '<b>X</b> — land here', 'hint');
        hint.style.color = blocker ? 'var(--ax-bad)' : 'var(--ax-good)';
        hint.style.display = 'block';
      } else hint.style.display = 'none';
      return;
    }
    if (this.mode === 'hover' && this.hoverItem) {
      const spec = HOVERCRAFT[this.hoverItem.key];
      const spd = Math.round(Math.hypot(this.velX, this.velZ));
      const drift = this.hoverDrifting ? ` · <span style="color:var(--ax-hi)">${this.hoverNatural ? 'SLIDE' : 'DRIFT'} ${Math.round(this.hoverDriftCharge * 100)}%</span>` : '';
      const wake = this.hoverOnWater ? ' · <span style="color:var(--ax-hi)">WAKE 30</span>' : '';
      const turbo = this.hoverTurboPrev > 0 ? ' · <span style="color:var(--ax-good)">TURBO!</span>' : '';
      this.setHudHtml(hud, `⛵ ${spec.name} · ${spd} m/s${drift}${turbo}${wake}`
        + ` · <span style="color:var(--ax-warn)">boost ${Math.round(this.boosterE)}</span> · turn hard to slide (SPACE forces it) · SHIFT boost · X off`, 'hud');
      hud.style.display = 'block';
      hint.style.display = 'none';
      return;
    }
    hud.style.display = 'none';
    // Aboard the sky-ship the city core sits 760 m below (nearCity is XZ-only):
    // no hangar/berth hints for a hull nobody can reach from the decks.
    if (!atlas.spaceFlight && !atlas.loadingGate && this.mode === 'none' && !aboardSkyShip()) {
      if (this.inCommons()) {
        if (this.nearOwnBerth()) { this.setHudHtml(hint, '<b>X</b> — board your ship &amp; lift off', 'hint'); hint.style.color = 'var(--ax-hi)'; hint.style.display = 'block'; return; }
        if (this.nearCity() && atlas.conn?.identity && hasActiveShip()) { this.setHudHtml(hint, '<b>X</b> — lift off (city hangar) · <b>Shift+X</b> — recall your ship', 'hint'); hint.style.color = 'var(--ax-hi)'; hint.style.display = 'block'; return; }
      } else if (this.onGroundWorld() && atlas.conn?.identity && hasActiveShip()) {
        // A personal / spawned world: lift the ship off in place (no berth needed).
        this.setHudHtml(hint, '<b>Shift+X</b> — lift your ship into this sky', 'hint'); hint.style.color = 'var(--ax-hi)'; hint.style.display = 'block'; return;
      }
    }
    hint.style.display = 'none';
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('wheel', this.onWheel); // (bug sweep) the passive:false handler outlived the module and kept swallowing scroll
    if (this.vfx) { this.vfx.root.parent?.remove(this.vfx.root); this.vfx.dispose(); this.vfx = null; } // (bug sweep) live sprites stayed parented to the scene
    this.clearBolts();
    const scene0 = this.app?.world.scene;
    for (const b of this.boltPool) scene0?.remove(b); // shared-material sprites — no dispose
    this.boltPool = [];
    cloudTunnel.dispose(scene0 ?? null);
    this.clearRideMesh();
    const scene = this.app?.world.scene;
    for (const [, e] of this.berthMeshes) { scene?.remove(e.obj); deferDisposeTree(e.obj); }
    this.berthMeshes.clear();
    for (const [, r] of this.remoteRigs) { r.obj.removeFromParent(); deferDisposeTree(r.obj); }
    this.remoteRigs.clear();
    for (const [, rf] of this.remoteFx) rf.fx.dispose((x) => disposeLater(x));
    this.remoteFx.clear();
    this.hud?.remove();
    this.hint?.remove();
    atlas.hoverRide = false;
    atlas.skyFlight = false;
  }
}

/** Does the local player own an active ship? (berth prompts gate on it) */
function hasActiveShip(): boolean {
  const conn = atlas.conn;
  if (!conn?.identity) return false;
  try {
    for (const s of conn.db.playerShip.iter()) {
      if (s.owner.isEqual(conn.identity) && s.active) return true;
    }
  } catch { /* row cache mid-churn */ }
  return false;
}

